export type Lang = "en" | "nl";

type Audience = { tab: string; title: string; desc: string; bullets: string[] };
type LegalTable = { caption?: string; headers: string[]; rows: string[][] };
type LegalDefinition = { term: string; def: string };
type LegalSubsection = { h: string; body?: string; bullets?: string[] };
type LegalSection = {
  h: string;
  body?: string;
  bullets?: string[];
  subsections?: LegalSubsection[];
  tables?: LegalTable[];
  definitions?: LegalDefinition[];
};
type LegalDoc = {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
  history?: { version: string; date: string; note: string }[];
  contact?: { label: string; value: string }[];
};
type SolutionSection = { title: string; body: string; bullets?: string[] };
type SolutionDoc = {
  hero: { eyebrow: string; title: string; lead: string; cta1: string; cta2: string; image: "broker" | "supplier" };
  intro: { what: string; why: string };
  benefits: { title: string; items: { t: string; d: string; tone: "mint" | "coral" | "butter" | "lavender" }[] };
  deepDive: { eyebrow: string; title: string; lead: string; sections: SolutionSection[] };
  steps: { title: string; items: { num: string; t: string; d: string }[] };
  faq: { title: string; items: { q: string; a: string }[] };
  notReplaces?: string;
};

type Dict = {
  nav: {
    platform: string;
    marketplaces: string;
    ai: string;
    pricing: string;
    contact: string;
    register: string;
    login: string;
    about: string;
  };
  hero: {
    eyebrow: string;
    title: string;
    titleAccent: string;
    subtitle: string;
    cta1: string;
    cta2: string;
    badge: string;
  };
  pillars: {
    title: string;
    subtitle: string;
    items: { title: string; desc: string; tone: "mint" | "coral" | "butter" | "lavender" }[];
  };
  audiences: { eyebrow: string; title: string; desc: string; tabs: Audience[] };
  flow: {
    eyebrow: string;
    title: string;
    desc: string;
    steps: { num: string; title: string; desc: string; tone: "mint" | "coral" | "butter" | "lavender" }[];
  };
  broker: {
    eyebrow: string;
    title: string;
    desc: string;
    bullets: string[];
    invitedTitle: string;
    invitedSuppliers: { name: string; tag: string; status: "active" | "invited" }[];
    marginLabel: string;
    storefronts: { name: string; tag: string; tone: "mint" | "coral" | "butter" | "lavender" | "sky" }[];
    relationshipTitle: string;
    relationshipDesc: string;
    scaleTitle: string;
    scaleItems: { t: string; d: string }[];
  };
  supplier: {
    eyebrow: string;
    title: string;
    desc: string;
    bullets: string[];
    stripeLabel: string;
    stripeStatus: string;
    productExample: { name: string; price: string; lead: string };
    tile1Title: string; tile1Desc: string;
    tile2Title: string; tile2Desc: string;
    tile3Title: string; tile3Desc: string;
    youDecide: string;
    dashboardTitle: string;
    dashboardDesc: string;
    premiumTitle: string;
    premiumDesc: string;
    premiumBullets: string[];
    premiumNote: string;
  };
  sustainability: {
    eyebrow: string;
    title: string;
    desc: string;
    items: { title: string; desc: string }[];
  };
  how: { title: string; subtitle: string; steps: { t: string; d: string }[] };
  payments: {
    eyebrow: string;
    title: string;
    desc: string;
    bullets: string[];
    mock: { title: string; live: string; delta: string; rows: { who: string; what: string; amt: string }[] };
  };
  ai: {
    eyebrow: string;
    title: string;
    desc: string;
    bullets: string[];
    modules: { title: string; desc: string; tone: "mint" | "coral" | "butter" | "lavender" }[];
    planTitle: string;
    planItems: { when: string; what: string }[];
    futureNote: string;
    mockAlt: string;
  };
  pricing: {
    eyebrow: string;
    title: string;
    desc: string;
    columns: { who: string; price: string; sub: string; tone: "mint" | "coral" | "butter" | "lavender" }[];
    feeBadge: string;
    feeNote: string;
    includes: string;
    bullets: string[];
    cta: string;
    secondaryCta: string;
  };
  faq: { title: string; items: { q: string; a: string }[] };
  cta: { title: string; desc: string; button: string; secondary: string };
  hero_mock: {
    url: string;
    shopName: string;
    items: { name: string; price: string; tone: string }[];
    payoutLabel: string;
    payoutAmount: string;
    aiLabel: string;
    aiSnippet: string;
    pill: string;
  };
  footer: {
    tagline: string;
    product: string;
    company: string;
    legal: string;
    rights: string;
    privacy: string;
    terms: string;
    cookies: string;
    security: string;
    dpa: string;
    contact: string;
    register: string;
    login: string;
    about: string;
    notTitle: string;
    notItems: string[];
  };
  demo: {
    title: string; desc: string;
    name: string; namePh: string;
    company: string; companyPh: string;
    email: string; emailPh: string;
    phone: string; phonePh: string;
    message: string; messagePh: string;
    submit: string; sending: string;
    success: string; error: string;
  };
  contact: { title: string; subtitle: string; emailLabel: string; addressLabel: string; kvkLabel: string; formTitle: string; submit: string; sending: string; success: string; error: string };
  os: {
    eyebrow: string;
    title: string;
    desc: string;
    items: { title: string; desc: string }[];
    note: string;
  };
  about: {
    title: string;
    lead: string;
    sections: { h: string; body: string }[];
  };
  solutionsNav: { label: string; broker: string; supplier: string };
  trust: {
    eyebrow: string;
    title: string;
    desc: string;
    items: { t: string; d: string }[];
    availability: string;
  };
  solutions: {
    broker: SolutionDoc;
    supplier: SolutionDoc;
  };
  privacy: LegalDoc;
  terms: LegalDoc;
  cookies: LegalDoc;
  security: LegalDoc;
  dpa: LegalDoc;
};

const sharedFooter = (en: boolean) => ({
  tagline: en
    ? "One white-label platform for everything around the home. Before, during and after the sale. Under your own brand."
    : "Eén white-label platform voor alles rond de woning. Vóór, tijdens en na de verkoop. Onder jouw eigen merk.",
  product: en ? "Product" : "Product",
  company: en ? "Company" : "Bedrijf",
  legal: en ? "Legal" : "Juridisch",
  rights: en ? "All rights reserved." : "Alle rechten voorbehouden.",
  privacy: en ? "Privacy" : "Privacy",
  terms: en ? "Terms" : "Voorwaarden",
  cookies: en ? "Cookies" : "Cookies",
  security: en ? "Security" : "Security",
  dpa: "DPA",
  contact: en ? "Contact" : "Contact",
  about: en ? "About" : "Over ons",
  register: en ? "Register free" : "Gratis registreren",
  login: en ? "Log in" : "Inloggen",
  notTitle: en ? "What Tribeo doesn't do" : "Wat doet Tribeo niet",
  notItems: en
    ? [
        "We don't sell houses. Your agent does.",
        "We don't give legal, financial or tax advice.",
        "Warranties and liability sit with the supplier or agent, not with us.",
      ]
    : [
        "Wij verkopen geen huizen. Dat doet jouw makelaar.",
        "Wij geven geen juridisch, financieel of fiscaal advies.",
        "Garantie en aansprakelijkheid liggen bij de leverancier of makelaar, niet bij ons.",
      ],
});

export const translations: Record<Lang, Dict> = {
  nl: {
    nav: {
      platform: "Platform",
      marketplaces: "Marketplaces",
      ai: "AI",
      pricing: "Prijzen",
      contact: "Contact",
      about: "Over ons",
      register: "Gratis registreren",
      login: "Inloggen",
    },
    hero: {
      eyebrow: "White-label platform voor makelaars",
      title: "Je deal is voorbij. Je klant",
      titleAccent: "niet",
      subtitle:
        "Eén plek voor alles rond de woning. Vóór, tijdens en na de verkoop. Onder jouw eigen merk, met leveranciers die jij hebt geselecteerd. Op elke bestelling pak je een marge. Geen abonnement, geen opstartkosten.",
      cta1: "Gratis registreren",
      cta2: "Demo aanvragen",
      badge: "Geen abonnement, geen opstartkosten",
    },
    pillars: {
      title: "Vier dingen die Tribeo voor jouw kantoor doet.",
      subtitle:
        "Van marketplace tot eigen huisdossier en advies op maat. Eén platform onder jouw eigen merk, voor de hele cyclus rond de woning.",
      items: [
        { title: "Marketplace onder jouw eigen merk", desc: "Eigen storefront, eigen domein en jouw eigen selectie van leveranciers. Per product bepaal jij de marge.", tone: "mint" },
        { title: "Property Hub voor jouw klant", desc: "Foto's, plattegronden, documenten en een tijdlijn van de woning op één plek. Bij verkoop overdraagbaar aan de nieuwe eigenaar.", tone: "coral" },
        { title: "AI Wooncoach", desc: "Persoonlijk advies over onderhoud, verbouwen en verduurzamen. Direct gekoppeld aan jouw leveranciers.", tone: "lavender" },
        { title: "Eén afrekening, alles geregeld", desc: "Betalingen, btw en uitbetalingen lopen automatisch. Jij hoeft geen facturen te sturen of na te jagen.", tone: "butter" },
      ],
    },
    audiences: {
      eyebrow: "Voor wie",
      title: "Voor het makelaarskantoor en de leverancier.",
      desc: "Tribeo is gratis te gebruiken. Geen abonnement, geen opstartkosten. Volledig transparant: de leverancier betaalt 6% over een geslaagde verkoop.",
      tabs: [
        {
          tab: "Makelaars",
          title: "Jouw eigen marketplace, op jouw domein.",
          desc: "Bouw een blijvende relatie met koper en verkoper via je eigen marketplace. Nodig zelf leveranciers uit of selecteer leveranciers die al op Tribeo aangesloten zijn.",
          bullets: [
            "Geen abonnement en geen opstartkosten",
            "Eigen domein, eigen huisstijl, eigen selectie van producten",
            "Onbeperkt marketplaces vanuit één account",
            "Bepaal per marketplace en per product je marge",
          ],
        },
        {
          tab: "Leveranciers",
          title: "Eén catalogus, meerdere marketplaces.",
          desc: "Krijg gratis een eigen webshop voor je eigen klanten. En word zichtbaar in de marketplaces van makelaars die jouw producten en diensten doorzetten naar kopers en verkopers.",
          bullets: [
            "Gratis eigen webshop voor je eigen klanten",
            "Word geselecteerd door makelaars in jouw werkgebied",
            "Bepaal zelf prijs, voorwaarden en levertijd",
            "Je betaalt pas bij een geslaagde verkoop (6%)",
          ],
        },
      ],
    },
    flow: {
      eyebrow: "Zo werkt het",
      title: "Voor koper en verkoper voelt het als één plek.",
      desc: "Koper en verkoper bestellen producten en diensten bij leveranciers die door de eigen makelaar zijn geselecteerd.",
      steps: [
        { num: "01", title: "Open de marketplace van je makelaar", desc: "Eigen huisstijl, eigen domein. Vertrouwd via de makelaar die jij al kent.", tone: "mint" },
        { num: "02", title: "Bestel of vraag een offerte aan", desc: "Lokale leveranciers met heldere prijzen, voorwaarden en levertijden.", tone: "coral" },
        { num: "03", title: "Afrekenen en klaar", desc: "Eén betaling. Factuur, btw en uitbetaling worden automatisch geregeld.", tone: "lavender" },
      ],
    },
    broker: {
      eyebrow: "Voor makelaars",
      title: "Jij bepaalt wie er in jouw marketplace staat.",
      desc: "Een marketplace onder jouw merk, met de leveranciers die jij hebt geselecteerd. Eigen logo, eigen kleuren, eigen domein. Voor jouw klant voelt het als een stuk van jouw kantoor.",
      bullets: [
        "Nodig zelf leveranciers uit of kies uit het bestaande netwerk",
        "Per product en per marketplace je eigen marge instellen",
        "Onbeperkt marketplaces vanuit één account",
        "White label: eigen logo, kleuren en domein",
      ],
      invitedTitle: "Geselecteerde leveranciers",
      invitedSuppliers: [
        { name: "Woningfotografie", tag: "Fotografie en video", status: "active" },
        { name: "Floorplanner", tag: "Plattegronden en 3D", status: "active" },
        { name: "Stylist Studio", tag: "Verkoopstyling", status: "invited" },
        { name: "Hovenier De Groene Hand", tag: "Tuinonderhoud", status: "active" },
      ],
      marginLabel: "Jouw marge",
      storefronts: [
        { name: "Marketplace woningpresentatie", tag: "Fotografie, plattegronden, styling", tone: "mint" },
        { name: "Marketplace verduurzaming", tag: "Isolatie, warmtepomp, zonnepanelen", tone: "lavender" },
        { name: "Marketplace nieuwbouwproject Zuiderpark", tag: "Inrichting en tuin", tone: "coral" },
      ],
      relationshipTitle: "Jij houdt de klantrelatie. Wij doen de logistiek.",
      relationshipDesc: "De koper, verkoper of eigenaar blijft jouw klant. Tribeo regelt het bestellen, betalen en uitbetalen op de achtergrond. Communicatie loopt via jouw merk, niet via ons.",
      scaleTitle: "Schaalt mee met je kantoor",
      scaleItems: [
        { t: "Solo makelaar", d: "Eén marketplace, eigen domein, klaar binnen tien minuten." },
        { t: "Kantoor met team", d: "Meerdere collega's, één gedeeld dashboard, gedeelde leveranciersselectie." },
        { t: "Meerdere vestigingen", d: "Een aparte marketplace per vestiging, regio of nieuwbouwproject." },
      ],
    },
    supplier: {
      eyebrow: "Voor leveranciers",
      title: "Klanten via warme verwijzingen, niet via dure leads.",
      desc: "Op Tribeo betaal je niks per klik en niks per offerte. Je verschijnt in de marketplaces van makelaars in jouw regio, op het moment dat hun klanten een tuin, schilderwerk, fotografie of isolatie nodig hebben. Eén catalogus, meerdere marketplaces, plus een gratis eigen webshop. Bij een geslaagde bestelling betaal je 6 procent. Geen verkoop is geen kosten.",
      bullets: [
        "Klanten via marketplaces van makelaars in jouw regio",
        "Gratis eigen webshop voor je eigen klanten",
        "Je betaalt pas bij een geslaagde verkoop (6%)",
        "Veilige uitbetaling op je zakelijke rekening",
      ],
      stripeLabel: "Betalingen",
      stripeStatus: "Verbonden",
      productExample: { name: "Woningfotografie + plattegrond", price: "€ 195", lead: "Op locatie binnen 3 werkdagen" },
      tile1Title: "Eigen webshop", tile1Desc: "Gratis pagina voor je eigen klanten.",
      tile2Title: "Klanten via makelaars", tile2Desc: "Makelaars zetten jou door naar kopers en verkopers.",
      tile3Title: "Veilige uitbetaling", tile3Desc: "Geld op je zakelijke rekening na elke order.",
      youDecide: "jij bepaalt",
      dashboardTitle: "Eén dashboard voor alle marketplaces",
      dashboardDesc: "Beheer je catalogus, prijzen en levertijden op één plek. Je producten verschijnen automatisch bij elke makelaar die jou heeft geselecteerd.",
      premiumTitle: "Optioneel: Premium zichtbaarheid",
      premiumDesc: "Wil je extra voorrang krijgen in de marketplaces van makelaars? Dan kun je later kiezen voor Premium. Voor de meeste leveranciers is gratis meedoen meer dan genoeg.",
      premiumBullets: [
        "Hogere positie in zoekresultaten van marketplaces",
        "Aanbevolen badge bij relevante producten",
        "Inzicht in welke makelaars jouw catalogus tonen",
      ],
      premiumNote: "Premium komt later. Begin gratis, je kunt altijd later kiezen.",
    },
    sustainability: {
      eyebrow: "Duurzaam wonen",
      title: "Subsidie, isolatie, warmtepomp — uitgezocht voor déze woning.",
      desc: "Tribeo combineert AI advies met leveranciers die door de makelaar zijn geselecteerd. Van isolatie tot warmtepomp tot zonnepanelen, inclusief subsidie check.",
      items: [
        { title: "Isolatie advies", desc: "Welk pakket loont voor jouw woning en wijk?" },
        { title: "Warmtepomp check", desc: "Past hij bij jouw bouwjaar en energieverbruik?" },
        { title: "Zonnepanelen", desc: "Berekening van opbrengst, terugverdientijd en subsidies." },
        { title: "Subsidie radar", desc: "Altijd actuele subsidies voor jouw situatie." },
      ],
    },
    how: {
      title: "Zo werkt het.",
      subtitle: "Van aanmelding tot eerste bestelling. Geen ontwikkelaars, geen integraties.",
      steps: [
        { t: "Maak gratis een account", d: "Naam, e-mail, KVK. Klaar binnen tien minuten. Geen creditcard, geen contract." },
        { t: "Selecteer je leveranciers", d: "Nodig je eigen partners uit, of kies uit het netwerk van leveranciers die al op Tribeo zijn aangesloten. Per marketplace bepaal jij wie zichtbaar is en met welke marge." },
        { t: "Verwijs koper en verkoper door", d: "Plak de link onder je e-mailhandtekening, in je nieuwsbrief en op de bevestigingsmail van je dossier. Vanaf dat moment bestellen je klanten via jouw marketplace." },
        { t: "Verdien per bestelling", d: "Bij elke transactie wordt jouw marge automatisch op je rekening gestort. Tribeo regelt de rest." },
      ],
    },
    payments: {
      eyebrow: "Betalingen",
      title: "Betalen, splitsen en uitbetalen. Geregeld.",
      desc: "Eén betaling van de klant. Twee uitbetalingen, automatisch. Marketplace en leverancier krijgen wat ze toekomt — zonder factuur-pingpong.",
      bullets: [
        "Gesplitste betalingen tussen marketplace en leverancier",
        "Eenvoudige onboarding voor leveranciers",
        "Automatische facturatie en btw exports",
        "Realtime financiële dashboards",
      ],
      mock: {
        title: "Uitbetalingen deze week",
        live: "Live",
        delta: "+12,4% vs vorige week",
        rows: [
          { who: "Installatiebedrijf", what: "CV keuring", amt: "€ 85,00" },
          { who: "Glaszettersbedrijf", what: "HR++ glas", amt: "€ 2.400,00" },
          { who: "Hovenier", what: "Tuinonderhoud", amt: "€ 450,00" },
        ],
      },
    },
    ai: {
      eyebrow: "AI voor de woning",
      title: "ChatGPT kent jouw huis niet. Tribeo wel.",
      desc: "Bouwjaar, energielabel, oppervlakte, staat van de cv-ketel — allemaal beschikbaar omdat de makelaar koppelt. De koper stelt een vraag over zíjn huis, Tribeo antwoordt op basis van zíjn huis. En verwijst naar leveranciers die de makelaar al heeft gescreend.",
      bullets: [
        "Direct antwoord op vragen over jouw woning",
        "Slim onderhoudsplan op basis van bouwjaar en staat",
        "Verduurzamingsadvies inclusief subsidies",
        "Geselecteerde leveranciers staan klaar",
      ],
      modules: [
        { title: "AI Wooncoach", desc: "Vraag alles over je huis, van bouwjaar tot energielabel.", tone: "mint" },
        { title: "AI Verbouwen", desc: "Ideeën, kostenrange en leveranciers in één gesprek.", tone: "coral" },
        { title: "Slim Onderhoudsplan", desc: "Tijdlijn met onderhoud op basis van leeftijd en staat.", tone: "butter" },
        { title: "AI Verduurzaming", desc: "Routekaart voor isolatie, warmtepomp, zonnepanelen en subsidies.", tone: "lavender" },
      ],
      planTitle: "Slim Onderhoudsplan",
      planItems: [
        { when: "Q4 2026", what: "Schilderwerk gevel. Tijd voor een check." },
        { when: "Q2 2027", what: "CV ketel vervangen (15 jaar oud)." },
        { when: "2028", what: "Dakisolatie. Terugverdientijd circa 6 jaar." },
      ],
      futureNote: "Binnenkort: woningpaspoort en Vision AI. Scan een kamer met je telefoon en krijg direct meubels van jouw leveranciers.",
      mockAlt: "AI Wooncoach in een telefoon mockup",
    },
    pricing: {
      eyebrow: "Prijzen",
      title: "Geen abonnement. Geen opstartkosten. Geen contract.",
      desc: "Het platform is gratis. De leverancier betaalt 6% bij een geslaagde verkoop. Geen verkoop is geen kosten. Stoppen kan op elk moment, zonder opzegtermijn.",
      columns: [
        { who: "Makelaar", price: "€ 0", sub: "Geen abonnement, geen opstartkosten.", tone: "mint" },
        { who: "Koper en verkoper", price: "€ 0", sub: "Gratis bestellen en betalen.", tone: "lavender" },
        { who: "Leverancier", price: "6%", sub: "Alleen bij een geslaagde verkoop.", tone: "coral" },
      ],
      feeBadge: "6% transactiekosten",
      feeNote: "Je betaalt pas als jouw klanten transacties doen. Betaalkosten kunnen apart van toepassing zijn.",
      includes: "Alles zit erbij:",
      bullets: [
        "Onbeperkt eigen marketplaces",
        "Onbeperkt leveranciers en producten",
        "Veilige betalingen en uitbetalingen",
        "AI Wooncoach, Verbouwen, Onderhoudsplan en Verduurzaming",
        "Dashboards en rapportages",
        "E-mail en chat ondersteuning",
      ],
      cta: "Gratis starten",
      secondaryCta: "Bekijk een demo",
    },
    faq: {
      title: "Veelgestelde vragen",
      items: [
        { q: "Wat kost Tribeo voor een makelaar?", a: "Niks. Geen abonnement, geen opstartkosten, geen verborgen kosten. Je gebruikt het platform gratis en bepaalt zelf je marge per product. Die marge wordt bij elke transactie automatisch op je rekening gestort. De 6 procent transactiekosten worden ingehouden bij de leverancier, niet bij jou." },
        { q: "Hoe ontvangen leveranciers betalingen?", a: "Bij registratie koppelt de leverancier eenmalig zijn zakelijke rekening aan Stripe. Daarna wordt elke bestelling automatisch verwerkt. De klant betaalt, het geld wordt gesplitst tussen marketplace en leverancier, en alles komt op de juiste rekening terecht. De leverancier krijgt heldere rapportages en btw klare exports rechtstreeks uit het dashboard. Geen handmatig factureren, geen herinneringen sturen." },
        { q: "Wie selecteert de leveranciers?", a: "De makelaar, volledig zelf. Je nodigt je eigen partners uit (hovenier, schilder, fotograaf, keukenstudio) of je kiest uit het netwerk van leveranciers dat al op Tribeo is aangesloten. Per marketplace bepaal jij wie zichtbaar is en met welke marge. Tribeo bemoeit zich niet met die keuze, behalve een basale check op KVK en Stripe verificatie." },
        { q: "Voor welke woningen is Tribeo geschikt?", a: "Voor zo goed als alles waar een makelaar bij betrokken is. Bestaande bouw, nieuwbouw, appartementen, vrijstaande woningen, particulier of zakelijk. Of je nu één marketplace voor je hele kantoor wil, of een aparte marketplace per nieuwbouwproject of regio, het werkt." },
        { q: "Wat doet de AI Wooncoach precies?", a: "De Wooncoach beantwoordt vragen van koper en verkoper over hun woning. Onderhoud, verbouwen, verduurzamen, kosten, tijdlijnen. Het verschil met een algemene chatbot is dat de Wooncoach de woning kent. Bouwjaar, oppervlakte, energielabel, leeftijd van de cv ketel. Die context komt rechtstreeks van de makelaar. De Wooncoach geeft adviezen, geen besluiten met juridische gevolgen. Onder de motorkap draait Claude van Anthropic." },
        { q: "Krijgt de leverancier woningcontext bij een offerte?", a: "Alleen wat relevant is voor de offerte, en alleen na akkoord van de eindklant. Een hovenier krijgt het tuinoppervlak, een isolatiebedrijf het bouwjaar en de gevelmaten. Zo kan de leverancier een scherpe en realistische offerte maken, zonder dat er meer wordt gedeeld dan nodig." },
        { q: "Is mijn data veilig?", a: "Ja. Versleuteling bij verzending en opslag, multi factor authenticatie op productiesystemen, hosting binnen de EU bij AWS en Google. Betalingen lopen via een PCI DSS Level 1 gecertificeerde betaalpartner. We zijn open over wat we wel en niet doen: de volledige beschrijving staat op de Security pagina." },
      ],
    },
    cta: {
      title: "Je klant van vorig jaar staat over zes maanden in de bouwmarkt.",
      desc: "Maak nu de marketplace waar hij liever bestelt. Tien minuten werk, geen creditcard nodig.",
      button: "Gratis starten",
      secondary: "Demo aanvragen",
    },
    hero_mock: {
      url: "marketplace.jouwmakelaar.nl",
      shopName: "Jouw Marketplace",
      items: [
        { name: "Woningfotografie", price: "€195", tone: "mint" },
        { name: "Plattegrond + 3D", price: "€95", tone: "butter" },
        { name: "Verkoopstyling", price: "€450", tone: "coral" },
        { name: "Verhuisadvies", price: "Gratis", tone: "lavender" },
      ],
      payoutLabel: "Uitbetaling vandaag",
      payoutAmount: "€ 4.280,00",
      aiLabel: "AI Wooncoach",
      aiSnippet: "Je cv-ketel is bijna 15 jaar oud. Tijd voor onderhoud?",
      pill: "Geen abonnement. 6% per transactie.",
    },
    footer: sharedFooter(false),
    demo: {
      title: "Vraag je persoonlijke demo aan",
      desc: "Vul je gegevens in. We nemen binnen één werkdag contact met je op.",
      name: "Naam", namePh: "Voor en achternaam",
      company: "Bedrijf", companyPh: "Bedrijfsnaam",
      email: "E-mail", emailPh: "jij@bedrijf.nl",
      phone: "Telefoon (optioneel)", phonePh: "+31 …",
      message: "Bericht", messagePh: "Vertel ons kort over je situatie",
      submit: "Demo aanvragen", sending: "Versturen…",
      success: "Bedankt. We nemen binnen één werkdag contact met je op. Check je inbox voor een bevestiging.",
      error: "Er ging iets mis. Controleer je gegevens en probeer het opnieuw.",
    },
    contact: {
      title: "Neem contact op",
      subtitle: "We reageren binnen één werkdag. Voor demo's, partnerships en algemene vragen.",
      emailLabel: "E-mail",
      addressLabel: "Adres",
      kvkLabel: "KVK",
      formTitle: "Stuur ons een bericht",
      submit: "Verstuur formulier",
      sending: "Versturen…",
      success: "Bedankt. We nemen binnen één werkdag contact met je op. Check je inbox voor een bevestiging.",
      error: "Er ging iets mis. Controleer je gegevens en probeer het opnieuw.",
    },
    os: {
      eyebrow: "Eén platform",
      title: "Voor makelaars en leveranciers, samen.",
      desc: "Een marketplace in jouw huisstijl, betalingen, AI Wooncoach en webshops voor je leveranciers. Tribeo verbindt vraag en aanbod onder jouw merk als makelaar.",
      items: [
        { title: "Marketplace", desc: "Eigen domein, eigen huisstijl, eigen selectie van producten." },
        { title: "Betalingen", desc: "Splitsen en uitbetalen, volledig automatisch." },
        { title: "AI Wooncoach", desc: "Antwoorden, onderhoudsplan en verduurzamingsadvies." },
        { title: "Webshop voor leveranciers", desc: "Elke leverancier krijgt gratis een eigen webshop." },
      ],
      note: "Tribeo brengt makelaars, leveranciers, kopers en verkopers samen onder het merk van het makelaarskantoor.",
    },
    about: {
      title: "Over Tribeo",
      lead: "Een makelaar verkoopt een huis één keer. Daarna begint voor de koper een nieuw hoofdstuk: tienduizenden euro's aan styling, fotografie, een nieuwe keuken, een hovenier, isolatie en zonnepanelen. Op het moment van de sleuteloverdracht is de makelaar de meest vertrouwde adviseur die de klant heeft. Tribeo zorgt dat die rol blijft. Een marketplace onder het merk van de makelaar, met leveranciers die de makelaar zelf heeft geselecteerd. Voor de klant één plek waar hij alles kan regelen, voor de makelaar een logisch verlengstuk van zijn dienstverlening, voor de leverancier klanten met budget en intentie. We hebben drie maanden met makelaars gesproken voordat we een regel code schreven. Het is ons opgevallen dat de gemiddelde makelaar zijn klanten beter kent dan welk vergelijkingsplatform dan ook.",
      sections: [
        { h: "Wat we doen", body: "We bouwen het platform waarop makelaars hun eigen marketplace runnen. Eigen merk, eigen domein, eigen leveranciers, eigen marges. Daarnaast bieden we leveranciers een gratis webshop en zorgen we dat zij vindbaar worden in de marketplaces van makelaars in hun werkgebied. De techniek erachter (betalingen, splitsing, AI Wooncoach, dashboards) draait op de achtergrond. Op je scherm zie je het kantoor van je makelaar, niet ons." },
        { h: "Waar we in geloven", body: "Eenvoud en transparantie zijn termen die elke SaaS startup gebruikt, dus dat zeggen wij niet. Wat we wel doen: geen abonnement en geen opstartkosten, niet als marketingtruc maar omdat we vinden dat een platform pas geld mag verdienen als het waarde levert. Onze inkomsten zitten op de transacties, niet op de lock in. Geen lange contracten, wil je stoppen, dan stop je, vandaag nog. Je catalogus en je data neem je mee. Geen verborgen marges. Wat de makelaar instelt is wat de makelaar krijgt. Wat de leverancier afspreekt is wat de leverancier ontvangt, min de 6 procent die wij rekenen. Geen tussenlaag, geen extra fee in het kleine grijs. Open techniek: Tribeo is API first, en alles wat wij intern gebruiken is gedocumenteerd voor jouw IT partner. We sluiten niemand op. Eerlijk over wat we niet hebben: we zijn een klein team in Den Haag. Geen ISO 27001, geen SOC 2, geen pentest programma. We werken op AWS en Google in de EU, betalen via Stripe, gebruiken Claude voor de AI Wooncoach. Op de Security pagina staat precies wat we wel en niet doen, zonder mooie woorden eromheen." },
        { h: "Voor wie we het doen", body: "Voor de makelaar die zijn klant ook na de transactie wil blijven helpen, met een platform dat het werk uit handen neemt. Voor de leverancier die nieuwe klanten wil bereiken via warme verwijzingen van makelaars, in plaats van betalen voor losse leads. En voor de koper die op één plek alles kan regelen wat bij zijn nieuwe huis hoort, via partijen die zijn makelaar zelf heeft geselecteerd." },
        { h: "Achter Tribeo", body: "Tribeo is een product van TNA B.V., een Nederlands bedrijf gevestigd aan de Santiagosingel 26 in Den Haag, ingeschreven bij de KvK onder nummer 75729814. We zijn onafhankelijk, niet gefinancierd door een grote partij in de vastgoedmarkt, en we hosten in de EU. Vragen, demo of een gesprek? hello@tribeo.co. We reageren binnen één werkdag." },
      ],
    },
    trust: {
      eyebrow: "Veilig en transparant",
      title: "Geen mooie woorden, gewoon de basis op orde.",
      desc: "We zijn een klein team. We claimen niets wat we niet kunnen waarmaken. Dit is wat we wel doen.",
      items: [
        { t: "Hosting in de EU", d: "Je data staat op servers binnen de Europese Unie." },
        { t: "Versleuteld bij verzending en opslag", d: "Standaard, zonder extra instellingen." },
        { t: "AVG conform", d: "Gegevensverwerking volgens Europese privacyregels, met een DPA op verzoek." },
        { t: "Veilige betalingen", d: "Betalingen via een PCI DSS Level 1 gecertificeerde betaalpartner." },
      ],
      availability: "Tribeo is nu live in Nederland. Uitrol naar de rest van de EU staat op de planning.",
    },
    solutionsNav: {
      label: "Oplossingen",
      broker: "Voor makelaars",
      supplier: "Voor leveranciers",
    },
    solutions: {
      broker: {
        hero: {
          eyebrow: "Voor makelaars",
          title: "Je deal is voorbij. Je klant niet.",
          lead: "De koper die jij vorige maand aan een huis hebt geholpen, koopt komend jaar een keuken, een schilder, een hovenier, isolatie en een nieuwe vloer. Tienduizenden euro's, en jij ziet er nu niks van terug. Met je eigen Tribeo marketplace bied je dat werk aan onder jouw merk, met leveranciers die jij hebt geselecteerd. Op elke bestelling pak je een marge. Geen abonnement, geen opstartkosten.",
          cta1: "Gratis registreren",
          cta2: "Demo aanvragen",
          image: "broker",
        },
        intro: {
          what: "Een marketplace onder jouw merk, met de leveranciers die jij hebt geselecteerd. Eigen logo, eigen kleuren, eigen domein. Voor jouw klant voelt het als een stuk van jouw kantoor.",
          why: "De jaren na de sleuteloverdracht zijn voor de meeste kopers duurder dan de aankoop zelf. Verbouwen, onderhoud, verduurzamen, inrichten. Tribeo geeft jou de plek waar jouw klant dat regelt: jouw shortlist van leveranciers, jouw merk, jouw marge. Eén logische volgende stap voor de klant, jaren omzet voor jou.",
        },
        benefits: {
          title: "Waarom Tribeo voor jouw makelaarskantoor",
          items: [
            { t: "Eén deal, jaren omzet", d: "Een gemiddeld dossier levert je één keer courtage op. In de tien jaar daarna geeft die klant tienduizenden euro's uit aan zijn woning. Met een eigen marketplace verdien je op die uitgaven mee, jaar na jaar.", tone: "mint" },
            { t: "Verdienen zonder werk erbij", d: "Marge instellen kost een klik per product. Daarna doet het zichzelf. Bij elke bestelling stort Tribeo jouw deel automatisch op je rekening, factuur en btw worden voor je geregeld.", tone: "coral" },
            { t: "Jouw shortlist, jouw kwaliteit", d: "Jij selecteert de leveranciers, jij bepaalt de prijs en jij beslist wie wordt toegelaten. Zo weet je zeker dat de partijen die jouw klant ziet, het vertrouwen waardig zijn dat jouw kantoor heeft opgebouwd.", tone: "lavender" },
            { t: "Volledig in jouw merk", d: "Eigen logo, eigen kleuren, eigen domein. De marketplace draagt jouw naam, niet die van ons. Tribeo werkt op de achtergrond.", tone: "butter" },
          ],
        },
        deepDive: {
          eyebrow: "Hoe het werkt onder de motorkap",
          title: "Wat je krijgt zodra je een account aanmaakt.",
          lead: "Vier onderdelen die samen jouw marketplace tot een verlengstuk van je kantoor maken.",
          sections: [
            {
              title: "Jouw merk, overal zichtbaar",
              body: "Eigen subdomein of volledig eigen domein. Aanpasbare huisstijl en typografie tot in het lettertype van je e-mails. Per marketplace stel je zelf in welke leveranciers en producten zichtbaar zijn. Voor jouw klant voelt het als jouw eigen platform, omdat het dat ook is.",
              bullets: ["Eigen subdomein of volledig eigen domein", "Aanpasbare huisstijl en typografie", "Eigen tone of voice in teksten en e-mails"],
            },
            {
              title: "Onbeperkt marketplaces vanuit één account",
              body: "Een aparte marketplace per nieuwbouwproject. Eén voor verduurzaming. Eén per regio of per filiaal. Eén voor verkopers en eén voor kopers, als je dat wil. Elke marketplace heeft zijn eigen leveranciers, eigen marges en eigen huisstijl. Je beheert ze allemaal vanuit één dashboard.",
              bullets: ["Aparte marketplace per project of regio", "Per marketplace eigen leveranciers en marges", "Eén dashboard voor alles"],
            },
            {
              title: "Jij bepaalt je marge",
              body: "Je stelt per product en per marketplace zelf je marge in. Bij elke transactie wordt die automatisch verrekend en op je rekening gestort. Voorbeeld: de leverancier prijst verkoopstyling op 450 euro. Jij stelt je marge in op 15 procent. Je klant betaalt 517,50 euro. Tribeo stort 67,50 euro op jouw rekening, automatisch. De leverancier ontvangt zijn deel, na aftrek van 6 procent transactiekosten. Geen factuur die jij hoeft te sturen, geen herinnering die jij hoeft na te jagen.",
              bullets: ["Marge per product instelbaar", "Transparante uitbetalingen", "Btw klare exports"],
            },
            {
              title: "Werkt samen met de software die je al hebt",
              body: "Tribeo is opgebouwd als API first platform. We koppelen met de systemen waar je al in werkt: je CRM, je dossiermodule, je marketingtools. Open API, gedocumenteerd, en je data is van jou. Stuur de link gerust door aan je IT partner of bouwer, hij of zij weet wat ermee te doen. Wil je over een jaar overstappen, dan neem je alles netjes mee.",
              bullets: ["Open API voor koppelingen", "We sluiten je nergens op", "Documentatie voor je IT partner"],
            },
          ],
        },
        steps: {
          title: "Zo werkt het",
          items: [
            { num: "01", t: "Maak gratis een account", d: "Naam, e-mail, KVK. Klaar binnen tien minuten. Geen creditcard, geen contract." },
            { num: "02", t: "Selecteer je leveranciers", d: "Nodig je eigen partners uit (de schilder waar je al jaren mee werkt, de fotograaf van je woningen, de hovenier die je verkoopstyling doet) of kies uit het netwerk van leveranciers die al op Tribeo zijn aangesloten. Per marketplace bepaal jij wie zichtbaar is en met welke marge." },
            { num: "03", t: "Verwijs koper en verkoper door", d: "Plak de link onder je e-mailhandtekening, in je nieuwsbrief en op de bevestigingsmail van je dossier. Vanaf dat moment bestellen je klanten via jouw marketplace, zonder dat jij iets hoeft te doen." },
          ],
        },
        faq: {
          title: "Veelgestelde vragen",
          items: [
            { q: "Wat verdien ik per transactie?", a: "Wat jij wil. Je stelt je eigen marge in per product en per marketplace. Voorbeeld: de leverancier prijst verkoopstyling op 450 euro en jij zet je marge op 15 procent. Je klant betaalt 517,50 euro en Tribeo stort 67,50 euro op jouw rekening. Op fotografie, een keukenoffer of een isolatiepakket werkt het precies zo. De 6 procent transactiekosten worden ingehouden bij de leverancier, dus wat jij instelt is wat jij krijgt." },
            { q: "Wie regelt de leveranciers?", a: "Jij. Je nodigt je eigen partners uit of kiest leveranciers die al op Tribeo zijn aangesloten. Tribeo regelt de techniek en de betalingen, jij regelt het netwerk en bepaalt de marges." },
            { q: "Wat kost het?", a: "Voor jou als makelaar: nul. Geen abonnement, geen opstartkosten, geen contract. De 6 procent transactiekosten worden ingehouden bij de leverancier, niet bij jou." },
            { q: "Kan ik meerdere marketplaces aanmaken?", a: "Ja, onbeperkt. Eén voor je hele kantoor, of een aparte marketplace per nieuwbouwproject, regio of filiaal. Elke marketplace heeft een eigen selectie leveranciers, eigen marges en eigen huisstijl. Je beheert alles vanuit één dashboard." },
            { q: "Koppelt Tribeo met mijn bestaande systemen?", a: "Ja. We zijn API first opgebouwd en hebben een gedocumenteerde open API. Stuur de link gerust door aan je IT partner of bouwer, dan weet hij precies waar hij aan toe is. We sluiten je niet op. Je data is van jou en je komt er altijd weer uit, met export en al." },
          ],
        },
      },
      supplier: {
        hero: {
          eyebrow: "Voor leveranciers",
          title: "Klanten via warme verwijzingen, niet via dure leads.",
          lead: "Op Tribeo verschijn je in de marketplaces van makelaars in jouw regio, op het moment dat hun klanten een tuin, schilderwerk, fotografie of isolatie nodig hebben. Geen kosten per klik of per offerte. Eén catalogus, meerdere marketplaces, plus een gratis eigen webshop voor je eigen klanten. Bij een geslaagde bestelling betaal je 6 procent. Geen verkoop is geen kosten.",
          cta1: "Gratis registreren",
          cta2: "Demo aanvragen",
          image: "supplier",
        },
        intro: {
          what: "Een marketplace platform voor makelaars en leveranciers. Je vult je catalogus één keer in en bent vindbaar in alle marketplaces van makelaars die jou hebben geselecteerd. Daarbovenop krijg je een gratis eigen webshop voor klanten die rechtstreeks bij jou willen bestellen.",
          why: "Klanten via de makelaar weten wat ze willen. Ze zijn net verhuisd of staan op het punt te verhuizen, ze hebben budget en ze hebben een makelaar die jou aanbeveelt in plaats van iemand die je nog moet overtuigen. Geen koud bellen, geen prijsjagers, geen advertenties die geld kosten voordat er iemand boekt.",
        },
        benefits: {
          title: "Wat je krijgt",
          items: [
            { t: "Gratis eigen webshop", d: "Een eigen pagina met je merk, je producten en je voorwaarden. Stuur klanten er rechtstreeks naartoe of zet hem naast je bestaande website. Werkt op laptop, tablet en telefoon, en je betaalt er niks voor.", tone: "mint" },
            { t: "Klanten via makelaars", d: "Makelaars in jouw werkgebied verwijzen kopers en verkopers naar hun marketplace, en daar sta jij in. Jouw aanbod komt op het scherm op het moment dat de klant het werk inplant.", tone: "coral" },
            { t: "Veilige uitbetaling", d: "Je koppelt eenmalig je zakelijke rekening aan Stripe. Daarna komt elke bestelling automatisch op je rekening. Geen factuur die je hoeft te sturen, geen herinnering die je hoeft na te jagen.", tone: "lavender" },
            { t: "Je betaalt pas bij verkoop", d: "6 procent op een geslaagde bestelling. Geen abonnement, geen opstartkosten, geen contract. Geen verkoop is geen kosten.", tone: "butter" },
          ],
        },
        deepDive: {
          eyebrow: "Eén keer invullen. Overal verkopen.",
          title: "Jouw catalogus, jouw prijzen, jouw voorwaarden.",
          lead: "Tribeo zorgt dat je opduikt op het moment dat de klant zoekt.",
          sections: [
            {
              title: "Eén catalogus, overal vindbaar",
              body: "Vul je producten en diensten één keer in. Ze verschijnen tegelijk in je eigen webshop en in alle marketplaces van makelaars die jou hebben geselecteerd. Wijzig je een prijs of een levertijd, dan wordt het overal direct doorgevoerd. Geen Excel rondsturen, geen tien plekken bijwerken.",
              bullets: ["Eén plek om je catalogus te beheren", "Automatisch zichtbaar in marketplaces van makelaars", "Eigen prijzen, voorwaarden en levertijden"],
            },
            {
              title: "Je eigen webshop, gratis",
              body: "Voor je eigen klanten krijg je een gratis webshop op een eigen pagina. Stuur klanten er rechtstreeks naartoe, gebruik hem naast je bestaande website, of als enige verkoopkanaal als je nog geen site hebt. Geen developer nodig, geen hostingrekening.",
              bullets: ["Eigen pagina met je merk", "Geen technische kennis nodig", "Werkt op laptop, tablet en telefoon"],
            },
            {
              title: "Veilige betalingen en uitbetalingen",
              body: "Je koppelt eenmalig je zakelijke rekening aan Stripe. Daarna gebeuren betaling en uitbetaling automatisch via een PCI DSS Level 1 gecertificeerde betaaldienst. Heldere rapportages en btw klare exports staan rechtstreeks in je dashboard, klaar voor je boekhouder.",
              bullets: ["Eenmalige koppeling van je rekening", "Automatische uitbetaling per order", "Btw klare facturen en exports"],
            },
            {
              title: "Eerlijk en transparant",
              body: "Geen abonnement en geen opstartkosten. Je betaalt 6 procent over een geslaagde bestelling. Geen verkoop is geen kosten. Stoppen mag op elk moment via je dashboard, je catalogus wordt dan direct uit alle marketplaces gehaald.",
              bullets: ["6 procent alleen bij geslaagde verkoop", "Geen contract, geen opzegtermijn", "Volledig transparant in je dashboard"],
            },
          ],
        },
        steps: {
          title: "Zo simpel begin je",
          items: [
            { num: "01", t: "Maak een gratis account", d: "Naam, mailadres, KVK. Klaar in twee minuten, geen creditcard nodig." },
            { num: "02", t: "Vul je producten in", d: "Foto, prijs, levertijd. Loop je vast, dan helpen we je op weg via mail of chat." },
            { num: "03", t: "Koppel je zakelijke rekening", d: "Eenmalig, via Stripe. Vanaf dat moment komt elke bestelling automatisch binnen." },
          ],
        },
        faq: {
          title: "Veelgestelde vragen",
          items: [
            { q: "Wat kost het?", a: "Geen abonnement, geen opstartkosten. Je betaalt 6 procent over een geslaagde bestelling. Geen verkoop is geen kosten. Je kan op elk moment stoppen via het dashboard." },
            { q: "Heb ik technische kennis nodig?", a: "Nee. Je vult je producten in (foto, prijs, levertijd), koppelt je zakelijke rekening aan Stripe en je staat live. De onboarding duurt zo'n twee minuten. Loop je vast, dan helpen we je op weg via mail of chat." },
            { q: "Krijg ik klanten van de makelaar?", a: "Niet automatisch, maar daar is het hele platform op gebouwd. Makelaars die jou hebben geselecteerd in hun marketplace, sturen kopers en verkopers in hun werkgebied naar jouw aanbod. Hoeveel dat oplevert hangt af van hoeveel makelaars je aansluit, hoe scherp je prijs en levertijd zijn, en hoeveel transacties die makelaars jaarlijks doen. Aangezien je pas betaalt bij een verkoop, kost het je niks om in meerdere marketplaces tegelijk zichtbaar te zijn." },
            { q: "Hoe word ik betaald?", a: "Bij elke geslaagde bestelling wordt het bedrag op je gekoppelde zakelijke rekening gestort, na aftrek van de 6 procent transactiekosten van Tribeo en eventuele Stripe kosten. Uitbetaling loopt automatisch volgens het Stripe schema, in de praktijk binnen een paar werkdagen. Facturen en btw klare exports staan rechtstreeks in je dashboard." },
            { q: "Wat als ik wil stoppen?", a: "Je beëindigt je account via het dashboard. Je catalogus wordt direct uit alle marketplaces gehaald, open bestellingen worden netjes afgehandeld. Geen exit fee, geen verborgen kosten bij vertrek." },
          ],
        },
      },
    },
    privacy: {
      title: "Privacyverklaring",
      updated: "Laatst bijgewerkt",
      intro:
        "Tribeo is een handelsnaam van TNA B.V. Deze privacyverklaring beschrijft welke persoonsgegevens wij verwerken wanneer je gebruikmaakt van het Tribeo platform, onze websites (tribeo.co, tribeo.lovable.app) en de marketplaces die makelaars onder hun eigen merk via Tribeo aanbieden. Wij verwerken persoonsgegevens in overeenstemming met de Algemene Verordening Gegevensbescherming (AVG/GDPR) en de Nederlandse Uitvoeringswet AVG.",
      sections: [
        {
          h: "1. Wie wij zijn",
          body: "Verwerkingsverantwoordelijke voor de in deze verklaring beschreven verwerkingen is TNA B.V., handelend onder de naam Tribeo, gevestigd aan de Santiagosingel 26, 2548HN te 's-Gravenhage, Nederland, ingeschreven bij de Kamer van Koophandel onder nummer 75729814. Voor privacyvragen, verzoeken van betrokkenen en klachten kun je contact opnemen via privacy@tribeo.co.",
        },
        {
          h: "2. Reikwijdte van deze verklaring",
          body: "Deze verklaring is van toepassing op:",
          bullets: [
            "Bezoek aan en gebruik van onze websites en marketingpagina's",
            "Het aanmaken en gebruiken van een Tribeo account (makelaar, leverancier, eindklant)",
            "Het gebruik van marketplaces die makelaars onder hun eigen merk via Tribeo aanbieden, voor zover Tribeo daarvoor verwerkingsverantwoordelijke is",
            "Het gebruik van de AI Wooncoach",
            "Communicatie met ons via formulieren, e-mail of support",
          ],
        },
        {
          h: "3. Definities",
          body: "Voor de leesbaarheid hanteren wij de volgende begrippen:",
          definitions: [
            { term: "Persoonsgegevens", def: "Alle informatie over een geïdentificeerde of identificeerbare natuurlijke persoon." },
            { term: "Verwerking", def: "Elke handeling met betrekking tot persoonsgegevens, zoals verzamelen, opslaan, raadplegen, doorgeven of wissen." },
            { term: "Verwerkingsverantwoordelijke", def: "De partij die het doel en de middelen van de verwerking bepaalt. Voor de in deze verklaring genoemde verwerkingen is dat TNA B.V." },
            { term: "Verwerker", def: "Een partij die persoonsgegevens verwerkt namens en in opdracht van de verantwoordelijke." },
            { term: "Betrokkene", def: "De natuurlijke persoon op wie de persoonsgegevens betrekking hebben." },
          ],
        },
        {
          h: "4. Categorieën persoonsgegevens",
          body: "Afhankelijk van hoe je Tribeo gebruikt, verwerken wij de volgende categorieën persoonsgegevens.",
          tables: [
            {
              caption: "Overzicht categorieën",
              headers: ["Categorie", "Voorbeelden", "Bron"],
              rows: [
                ["Account- en contactgegevens", "Naam, e-mailadres, telefoonnummer, functie, bedrijfsnaam, KVK-nummer", "Door jou verstrekt bij registratie"],
                ["Authenticatiegegevens", "Wachtwoord (gehasht), tweede factor, sessietokens", "Door jou aangemaakt"],
                ["Profiel- en bedrijfsgegevens", "Logo, omschrijving, vestigingsadres, openingstijden", "Door jou verstrekt"],
                ["Transactie- en factuurgegevens", "Bestellingen, prijzen, fees, btw, IBAN-referentie via betaaldienst", "Bij gebruik van marketplace"],
                ["Woninggegevens", "Adres, bouwjaar, oppervlakte, energielabel, huispaspoort", "Door eindklant of makelaar verstrekt"],
                ["AI Wooncoach gesprekken", "Vragen, antwoorden, geanonimiseerde context", "Door jou ingevoerd"],
                ["Communicatie", "E-mails, formulierberichten, support tickets", "Door jou verstrekt"],
                ["Technische gegevens", "IP-adres, browsertype, device, taalvoorkeur, paginabezoeken", "Automatisch verzameld"],
                ["Cookie- en analytics-data", "Geanonimiseerde gebeurtenissen na toestemming", "Automatisch, op basis van consent"],
              ],
            },
          ],
        },
        {
          h: "5. Doeleinden en grondslagen",
          body: "Wij verwerken persoonsgegevens uitsluitend voor onderstaande doelen, telkens op basis van een geldige grondslag uit artikel 6 AVG.",
          tables: [
            {
              headers: ["Doel", "Grondslag", "Bewaartermijn"],
              rows: [
                ["Account aanmaken en beheren", "Uitvoering overeenkomst (art. 6.1.b)", "Looptijd account + 24 maanden"],
                ["Leveren van het platform en marketplace", "Uitvoering overeenkomst", "Looptijd account + 24 maanden"],
                ["Afhandelen van transacties en facturatie", "Uitvoering overeenkomst en wettelijke verplichting", "7 jaar (fiscale bewaarplicht)"],
                ["Klantsupport en communicatie", "Uitvoering overeenkomst en gerechtvaardigd belang", "3 jaar na laatste contact"],
                ["Beveiliging, fraudepreventie en logging", "Gerechtvaardigd belang (art. 6.1.f)", "Maximaal 12 maanden"],
                ["Productverbetering en geanonimiseerde analyses", "Gerechtvaardigd belang", "Tot doel is bereikt"],
                ["Analytics en sitemeting", "Toestemming (art. 6.1.a)", "Tot intrekking, max 14 maanden"],
                ["Direct marketing aan zakelijke klanten", "Gerechtvaardigd belang met opt-out", "Tot afmelding"],
                ["Marketing aan prospects", "Toestemming", "Tot intrekking"],
                ["Voldoen aan wettelijke verplichtingen", "Wettelijke verplichting (art. 6.1.c)", "Wettelijk voorgeschreven"],
              ],
            },
          ],
        },
        {
          h: "6. Cookies en vergelijkbare technieken",
          body: "Wij gebruiken functionele cookies (altijd actief, voor taal, sessie en beveiliging) en analytische cookies die alleen worden geplaatst nadat je daarvoor toestemming hebt gegeven via onze cookiebanner. Wij gebruiken op dit moment geen marketing- of trackingcookies van derden. Zie voor details het cookiebeleid.",
        },
        {
          h: "7. AI Wooncoach",
          body: "De AI Wooncoach helpt eindklanten met vragen over onderhoud, verbouwing en verduurzaming. Wij gebruiken hiervoor Claude van Anthropic. Daarbij gelden de volgende uitgangspunten:",
          bullets: [
            "Anthropic verwerkt prompts onder hun eigen voorwaarden en privacyverklaring en gebruikt API-input volgens hun beleid niet standaard om hun modellen te trainen.",
            "Wij delen niet meer informatie met de AI-leverancier dan nodig is om een vraag te beantwoorden.",
            "Antwoorden van de AI Wooncoach zijn adviserend; wij nemen geen volledig automatische besluiten met juridische gevolgen.",
            "Wij gebruiken AI ook gericht in onze eigen werkprocessen, bijvoorbeeld als hulpmiddel bij het opstellen of reviewen van juridische documenten waar dat nuttig is.",
            "Je kunt verzoeken je gespreksgeschiedenis te verwijderen via privacy@tribeo.co.",
          ],
        },
        {
          h: "8. Marketing en communicatie",
          body: "Zakelijke klanten kunnen service- en productupdates ontvangen op basis van gerechtvaardigd belang, met afmeldlink in elke e-mail. Voor commerciële nieuwsbrieven aan prospects vragen wij vooraf toestemming. Je kunt je voorkeuren op elk moment aanpassen via de afmeldlink onder onze e-mails of door te mailen naar privacy@tribeo.co.",
        },
        {
          h: "9. Ontvangers en subverwerkers",
          body: "Wij delen persoonsgegevens met de volgende categorieën ontvangers, telkens onder een verwerkersovereenkomst:",
          tables: [
            {
              caption: "Belangrijkste subverwerkers",
              headers: ["Subverwerker", "Dienst", "Locatie", "Waarborg"],
              rows: [
                ["AWS", "Hosting, database, opslag", "EU (Frankfurt / Ierland)", "DPA + EU-hosting"],
                ["Google Cloud", "Aanvullende cloudinfrastructuur", "EU", "DPA + EU-hosting"],
                ["Resend", "Transactionele e-mail", "EU / VS", "DPA + SCC"],
                ["Anthropic (Claude)", "AI / LLM voor Wooncoach", "VS", "DPA + SCC"],
                ["Stripe", "Betalingen en uitbetalingen", "EU / VS", "PCI-DSS Level 1 + DPA"],
              ],
            },
          ],
        },
        {
          h: "10. Internationale doorgifte",
          body: "Wij hosten primair binnen de EU. Wanneer een subverwerker gegevens verwerkt buiten de Europese Economische Ruimte (EER), gebeurt dit alleen met passende waarborgen, zoals de Standaard Contractuele Bepalingen van de Europese Commissie of een geldig adequaatheidsbesluit, aangevuld met technische en organisatorische maatregelen waar nodig.",
        },
        {
          h: "11. Bewaartermijnen",
          body: "Wij bewaren persoonsgegevens niet langer dan noodzakelijk voor de doelen waarvoor ze zijn verzameld of zolang dit wettelijk vereist is. Zie de tabel onder doeleinden voor specifieke termijnen.",
        },
        {
          h: "12. Beveiliging",
          body: "Wij nemen passende technische en organisatorische maatregelen om persoonsgegevens te beschermen, zoals encryptie in transit en at rest, MFA voor productietoegang en logging. Tribeo is een klein team en heeft (nog) geen ISO- of SOC-certificering. Zie de Security-pagina voor wat wij wel en (nog) niet doen.",
        },
        {
          h: "13. Jouw rechten",
          body: "Onder de AVG heb je de volgende rechten ten aanzien van je persoonsgegevens:",
          subsections: [
            { h: "Inzage", body: "Je kunt opvragen welke persoonsgegevens wij van je verwerken." },
            { h: "Rectificatie", body: "Je kunt onjuiste of onvolledige gegevens laten corrigeren." },
            { h: "Verwijdering", body: "Je kunt verzoeken om verwijdering van persoonsgegevens als deze niet langer nodig zijn of als je toestemming intrekt." },
            { h: "Beperking", body: "Je kunt vragen om de verwerking tijdelijk te beperken, bijvoorbeeld terwijl een verzoek wordt beoordeeld." },
            { h: "Bezwaar", body: "Je kunt bezwaar maken tegen verwerking op basis van gerechtvaardigd belang of voor direct marketing." },
            { h: "Dataportabiliteit", body: "Je kunt je gegevens in een gestructureerd, gangbaar formaat ontvangen of laten overdragen aan een andere partij." },
            { h: "Toestemming intrekken", body: "Waar wij verwerken op basis van toestemming, kun je deze op elk moment intrekken zonder dat dit terugwerkende kracht heeft." },
            { h: "Klacht indienen", body: "Je kunt een klacht indienen bij de Autoriteit Persoonsgegevens." },
          ],
        },
        {
          h: "14. Hoe oefen je je rechten uit",
          body: "Stuur een verzoek naar privacy@tribeo.co. Wij reageren in beginsel binnen één maand. Indien wij meer tijd nodig hebben, laten wij dat tijdig weten. Wij kunnen vragen om aanvullende informatie ter verificatie van je identiteit voordat wij een verzoek inwilligen.",
        },
        {
          h: "15. Geautomatiseerde besluitvorming",
          body: "Wij maken geen gebruik van uitsluitend geautomatiseerde besluitvorming met juridische of vergelijkbare gevolgen voor betrokkenen. De AI Wooncoach geeft adviserende informatie; beslissingen blijven aan de gebruiker.",
        },
        {
          h: "16. Kinderen",
          body: "Het Tribeo platform is niet gericht op personen jonger dan 16 jaar. Wij verwerken niet bewust gegevens van minderjarigen zonder toestemming van een ouder of voogd. Mocht dit toch onbedoeld gebeuren, neem dan contact op zodat wij de gegevens kunnen verwijderen.",
        },
        {
          h: "17. Wijzigingen",
          body: "Wij kunnen deze privacyverklaring aanpassen wanneer onze dienst of de toepasselijke wetgeving wijzigt. Bij materiële wijzigingen informeren wij je vooraf, bijvoorbeeld via e-mail of een melding op het platform. De meest recente versie staat altijd op deze pagina.",
        },
        {
          h: "18. Klachten",
          body: "Wij horen het graag van je als je niet tevreden bent over hoe wij met je gegevens omgaan: privacy@tribeo.co. Daarnaast heb je altijd het recht een klacht in te dienen bij de Autoriteit Persoonsgegevens (autoriteitpersoonsgegevens.nl) of bij de toezichthouder in jouw EU-lidstaat.",
        },
      ],
      contact: [
        { label: "Verwerkingsverantwoordelijke", value: "TNA B.V., handelend als Tribeo" },
        { label: "Adres", value: "Santiagosingel 26, 2548HN 's-Gravenhage" },
        { label: "KVK", value: "75729814" },
        { label: "Privacy contact", value: "privacy@tribeo.co" },
        { label: "Security contact", value: "security@tribeo.co" },
      ],
      history: [
        { version: "v1.0", date: "5 mei 2026", note: "Eerste publicatie." },
      ],
    },
    terms: {
      title: "Algemene voorwaarden",
      updated: "Laatst bijgewerkt",
      intro:
        "Deze algemene voorwaarden zijn van toepassing op het gebruik van het Tribeo platform en de daaraan gerelateerde diensten, geleverd door TNA B.V. (handelend onder de naam Tribeo), gevestigd aan de Santiagosingel 26, 2548HN te 's-Gravenhage, KVK 75729814. Door een account aan te maken of het platform te gebruiken aanvaard je deze voorwaarden.",
      sections: [
        {
          h: "1. Definities",
          body: "In deze voorwaarden gelden de volgende definities:",
          definitions: [
            { term: "Tribeo / wij / ons", def: "TNA B.V., handelend onder de naam Tribeo." },
            { term: "Platform", def: "Het Tribeo SaaS-platform inclusief de marketplaces, dashboards, AI Wooncoach, betaalintegratie en API's." },
            { term: "Klant", def: "De zakelijke partij die met Tribeo een overeenkomst sluit (makelaar of leverancier) en een account heeft." },
            { term: "Makelaar", def: "Klant die het platform gebruikt om een eigen marketplace aan te bieden aan eindklanten." },
            { term: "Leverancier", def: "Klant die diensten of producten aanbiedt via één of meer marketplaces." },
            { term: "Eindklant", def: "De natuurlijke persoon (koper of verkoper van een woning) die via een marketplace bestelt of een offerte aanvraagt." },
            { term: "Overeenkomst", def: "De afspraak tussen Tribeo en de Klant, bestaande uit deze voorwaarden, een eventuele orderbevestiging en de toepasselijke DPA." },
            { term: "Transactiefee", def: "Het percentage dat Tribeo in rekening brengt over een geslaagde transactie tussen Eindklant en Leverancier." },
          ],
        },
        {
          h: "2. Toepasselijkheid en rangorde",
          body: "Deze voorwaarden gelden voor elke overeenkomst en elk gebruik van het platform. Algemene voorwaarden van de Klant worden uitdrukkelijk van de hand gewezen. Bij strijdigheid geldt de volgende rangorde: (1) een schriftelijke specifieke overeenkomst tussen partijen, (2) de Verwerkersovereenkomst (DPA), (3) deze algemene voorwaarden, (4) de productdocumentatie.",
        },
        {
          h: "3. Account en registratie",
          body: "Om het platform te gebruiken sluit Klant zelfstandig een account af. Klant garandeert dat alle bij registratie verstrekte gegevens juist en volledig zijn en houdt deze actueel. Het platform is uitsluitend bestemd voor zakelijk gebruik door rechtspersonen of zelfstandig ondernemers van 18 jaar en ouder. Klant is verantwoordelijk voor het geheim houden van inloggegevens en voor alle handelingen onder het account.",
        },
        {
          h: "4. Beschikbaarheid van het platform",
          body: "Tribeo levert het platform met redelijke inspanning en streeft naar een hoge beschikbaarheid. Wij kunnen geen ononderbroken toegang garanderen.",
          subsections: [
            { h: "Onderhoud", body: "Gepland onderhoud kondigen wij vooraf aan en plannen wij waar mogelijk buiten kantooruren." },
            { h: "Storingen", body: "Bij ongeplande storingen spannen wij ons in deze zo snel mogelijk te verhelpen. Wij zijn niet aansprakelijk voor de gevolgen van tijdelijke onbeschikbaarheid, behoudens opzet of grove schuld." },
            { h: "Wijzigingen functionaliteit", body: "Wij kunnen functionaliteit toevoegen, wijzigen of beëindigen. Wezenlijke beperkingen kondigen wij minimaal 30 dagen vooraf aan." },
          ],
        },
        {
          h: "5. Rol van Tribeo",
          body: "Tribeo levert uitsluitend technische infrastructuur waarmee Makelaars marketplaces kunnen aanbieden en Leveranciers hun aanbod kunnen verkopen. Tribeo is faciliterend en is uitdrukkelijk geen partij bij de overeenkomsten die via het platform tot stand komen tussen Leveranciers en Eindklanten (overwegend tussen consumenten en bedrijven). Tribeo is niet verantwoordelijk of aansprakelijk voor de geleverde of niet geleverde diensten of producten, voor de uitvoering, kwaliteit, veiligheid, juistheid of garanties daarvan. Tribeo geeft geen enkele garantie over het aanbod op het platform. Tribeo doet zijn best om kwaliteit op het platform te bewaken en is op basis van best effort beschikbaar om bij problemen mee te helpen, zonder daarvoor enige inspanningsverplichting met een bepaald resultaat op zich te nemen.",
        },
        {
          h: "6. Verplichtingen van de Klant",
          body: "Klant gebruikt het platform uitsluitend voor de overeengekomen zakelijke doeleinden en houdt zich aan toepasselijke wet- en regelgeving, waaronder consumentenrecht, fiscale regels en privacywetgeving. Klant is verantwoordelijk voor de inhoud die via het account wordt geplaatst.",
        },
        {
          h: "7. Verplichtingen van de Leverancier",
          body: "De Leverancier is volledig en uitsluitend zelf verantwoordelijk voor:",
          bullets: [
            "Juistheid van prijzen, omschrijvingen, levertijden en voorraadinformatie",
            "Levering van de bestelde diensten of producten conform afspraak",
            "Eventuele garanties, herroepingsrecht en consumentenrechten",
            "Klantenservice, klachtafhandeling en aftersales",
            "Naleving van fiscale en branche-specifieke verplichtingen",
            "Aansprakelijkheid jegens de Eindklant voor eigen handelen of nalaten",
          ],
        },
        {
          h: "8. Eindklanten en consumentenrecht",
          body: "Wanneer een Eindklant als consument een dienst of product koopt via een marketplace, gelden de wettelijke consumentenrechten jegens de Leverancier. De Leverancier is verantwoordelijk voor het naleven van het herroepingsrecht, de wettelijke conformiteit en eventuele garantie. Tribeo faciliteert uitsluitend de transactie via Stripe en kan op basis van best effort ondersteuning bieden bij escalaties, maar is geen partij bij de koopovereenkomst en draagt geen verantwoordelijkheid voor de uitkomst.",
        },
        {
          h: "9. Prijzen, fees en betaling",
          body: "Het platform kent geen abonnement en geen opstartkosten voor Makelaars, Leveranciers en Eindklanten. Betalingen verlopen via Stripe. Op betalingen via Stripe zijn naast deze voorwaarden de voorwaarden van Stripe van toepassing.",
          subsections: [
            { h: "Transactiefee", body: "Tribeo brengt een transactiefee van 6% in rekening over elke geslaagde transactie van een Leverancier via een marketplace, exclusief btw." },
            { h: "Stripe-kosten", body: "Kosten van Stripe kunnen apart van toepassing zijn en worden transparant getoond. De voorwaarden en het privacybeleid van Stripe gelden voor de betaalverwerking." },
            { h: "Uitbetaling", body: "Uitbetaling aan de Leverancier vindt plaats via Stripe, na aftrek van fees, op de door Leverancier in Stripe opgegeven zakelijke rekening, conform de Stripe-uitbetalingsschema's." },
            { h: "Btw en facturatie", body: "Facturen worden elektronisch ter beschikking gesteld. Klant zorgt voor juiste btw-gegevens." },
            { h: "Terugboekingen en chargebacks", body: "Bij terugbetalingen aan Eindklanten worden bijbehorende fees naar evenredigheid verrekend. Chargebacks worden door Stripe afgehandeld en kunnen worden doorbelast aan de verantwoordelijke Leverancier." },
          ],
        },
        {
          h: "10. Aanvaardbaar gebruik",
          body: "Het is verboden het platform te gebruiken voor onder meer:",
          bullets: [
            "Onrechtmatige, misleidende of inbreukmakende content",
            "Het omzeilen van de transactieafhandeling van Tribeo",
            "Geautomatiseerd uitlezen of scrapen zonder schriftelijke toestemming",
            "Reverse engineering, decompileren of pogingen tot het achterhalen van de broncode",
            "Verzending van spam of ongevraagde berichten via het platform",
            "Activiteiten die de beveiliging of integriteit van het platform schaden",
          ],
        },
        {
          h: "11. Intellectuele eigendom",
          body: "Alle intellectuele eigendomsrechten op het platform, de software, documentatie en de Tribeo-merken berusten bij TNA B.V. Klant krijgt voor de duur van de overeenkomst een niet-exclusief, niet-overdraagbaar gebruiksrecht. Klant en Leverancier behouden de rechten op door hen geüploade content en verlenen Tribeo een wereldwijde, royaltyvrije licentie om die content te gebruiken voor zover noodzakelijk voor de levering van de dienst.",
        },
        {
          h: "12. Vertrouwelijkheid",
          body: "Partijen behandelen vertrouwelijke informatie van de andere partij vertrouwelijk en gebruiken deze alleen voor de uitvoering van de overeenkomst. Deze verplichting blijft van kracht na beëindiging van de overeenkomst.",
        },
        {
          h: "13. Privacy en gegevensverwerking",
          body: "Voor de verwerking van persoonsgegevens verwijzen wij naar de privacyverklaring. Voor zover Tribeo persoonsgegevens verwerkt namens de Klant, sluiten partijen de Verwerkersovereenkomst (DPA) die integraal deel uitmaakt van de overeenkomst.",
        },
        {
          h: "14. Beveiliging",
          body: "Tribeo neemt passende technische en organisatorische maatregelen ter beveiliging van het platform en de daarop verwerkte gegevens. Zie de Security-pagina voor details.",
        },
        {
          h: "15. Garanties en disclaimer",
          body: "Tribeo levert het platform 'as is' en 'as available'. Wij garanderen niet dat het platform foutloos of ononderbroken werkt of geschikt is voor een specifiek doel buiten de gedocumenteerde functionaliteit. Voor zover wettelijk toegestaan worden alle impliciete garanties uitgesloten.",
        },
        {
          h: "16. Aansprakelijkheid",
          body: "De aansprakelijkheid van Tribeo is beperkt als volgt:",
          bullets: [
            "Aansprakelijkheid is per gebeurtenis of reeks samenhangende gebeurtenissen beperkt tot de transactiefees die Tribeo in de drie maanden voorafgaand aan de gebeurtenis van de Klant heeft ontvangen, met een maximum van € 10.000.",
            "Aansprakelijkheid voor indirecte schade, gevolgschade, gederfde winst, verlies van data of reputatieschade is uitgesloten.",
            "Beperkingen gelden niet bij opzet of bewuste roekeloosheid van Tribeo of haar bedrijfsleiding, of bij wettelijk verplichte aansprakelijkheid die niet kan worden uitgesloten.",
          ],
        },
        {
          h: "17. Vrijwaring",
          body: "Klant en Leverancier vrijwaren Tribeo voor aanspraken van derden, waaronder Eindklanten, die voortvloeien uit hun gebruik van het platform of uit door hen aangeboden diensten of producten.",
        },
        {
          h: "18. Overmacht",
          body: "Geen van partijen is aansprakelijk voor tekortkomingen die het gevolg zijn van overmacht, waaronder storingen bij subverwerkers, internetuitval, cyberaanvallen, overheidsmaatregelen of natuurrampen. Bij langdurige overmacht (meer dan 30 dagen) kunnen partijen de overeenkomst zonder schadevergoeding ontbinden.",
        },
        {
          h: "19. Looptijd en beëindiging",
          body: "De overeenkomst geldt voor onbepaalde tijd en is door beide partijen op elk moment opzegbaar via het dashboard, zonder opzegtermijn. Open bestellingen worden netjes afgehandeld; de catalogus van een leverancier wordt direct uit alle marketplaces gehaald. Tribeo kan het account met onmiddellijke ingang opschorten of beëindigen bij ernstige schending van deze voorwaarden, herhaaldelijke wanbetaling, fraude of misbruik.",
        },
        {
          h: "20. Wijziging van de voorwaarden",
          body: "Tribeo kan deze voorwaarden wijzigen. Wezenlijke wijzigingen worden ten minste 30 dagen vooraf bekendgemaakt via e-mail of via het platform. Indien de Klant de wijziging niet accepteert, kan hij de overeenkomst voor de ingangsdatum kosteloos opzeggen.",
        },
        {
          h: "21. Overdracht",
          body: "Klant mag rechten en verplichtingen onder de overeenkomst niet zonder voorafgaande schriftelijke toestemming van Tribeo overdragen aan een derde. Tribeo mag de overeenkomst overdragen in het kader van een fusie, overname of reorganisatie.",
        },
        {
          h: "22. Diversen",
          body: "Indien een bepaling van deze voorwaarden nietig of vernietigbaar is, blijven de overige bepalingen onverminderd van kracht. Het niet uitoefenen van een recht door Tribeo betekent geen afstand van dat recht. Deze voorwaarden vormen, samen met de DPA en eventuele orderbevestiging, de volledige overeenkomst tussen partijen.",
        },
        {
          h: "23. Toepasselijk recht en geschillen",
          body: "Op deze voorwaarden en op alle overeenkomsten met Tribeo is Nederlands recht van toepassing. Geschillen worden bij uitsluiting voorgelegd aan de bevoegde rechter te Den Haag, tenzij dwingend recht anders bepaalt.",
        },
      ],
      contact: [
        { label: "Aanbieder", value: "TNA B.V., handelend als Tribeo" },
        { label: "Adres", value: "Santiagosingel 26, 2548HN 's-Gravenhage" },
        { label: "KVK", value: "75729814" },
        { label: "Contact", value: "hello@tribeo.co" },
      ],
      history: [
        { version: "v1.0", date: "5 mei 2026", note: "Eerste publicatie." },
      ],
    },
    cookies: {
      title: "Cookiebeleid",
      updated: "Laatst bijgewerkt",
      intro: "Tribeo gebruikt cookies en vergelijkbare technieken om de site te laten werken en, alleen na jouw toestemming, gebruik te analyseren. We gebruiken momenteel geen marketing cookies.",
      sections: [
        { h: "1. Wat zijn cookies", body: "Kleine tekstbestanden op je apparaat om de site te laten werken en gebruik te begrijpen." },
        { h: "2. Categorieën", body: "Wij gebruiken:", bullets: ["Functioneel (altijd actief): taal, sessie. Maximaal 12 maanden.", "Analytisch (op toestemming): geanonimiseerde click tracking.", "Marketing: momenteel niet in gebruik."] },
        { h: "3. Derden", body: "Stripe plaatst noodzakelijke cookies voor checkout en fraudepreventie." },
        { h: "4. Toestemming intrekken", body: "Via je browserinstellingen of de cookie instellingen onderin." },
        { h: "5. Meer info", body: "Zie privacyverklaring. Vragen? hello@tribeo.co." },
      ],
    },
    dpa: {
      title: "Verwerkersovereenkomst (DPA)",
      updated: "Laatst bijgewerkt",
      intro:
        "Deze Verwerkersovereenkomst (Data Processing Agreement, hierna DPA) maakt integraal deel uit van de overeenkomst tussen TNA B.V. (handelend onder de naam Tribeo, hierna Verwerker) en de zakelijke klant (hierna Verantwoordelijke). De DPA beschrijft hoe Verwerker persoonsgegevens verwerkt namens Verantwoordelijke in overeenstemming met artikel 28 AVG. Bij strijdigheid prevaleert deze DPA boven de algemene voorwaarden voor zover het de verwerking van persoonsgegevens betreft.",
      sections: [
        {
          h: "1. Definities",
          body: "Begrippen uit de AVG hebben in deze DPA dezelfde betekenis. Aanvullend:",
          definitions: [
            { term: "AVG", def: "Verordening (EU) 2016/679 (Algemene Verordening Gegevensbescherming)." },
            { term: "Diensten", def: "Het Tribeo platform en daaraan verbonden ondersteunende diensten." },
            { term: "Subverwerker", def: "Een door Verwerker ingeschakelde derde die persoonsgegevens verwerkt namens Verantwoordelijke." },
            { term: "Datalek", def: "Een inbreuk op de beveiliging die per ongeluk of onrechtmatig leidt tot de vernietiging, het verlies, de wijziging of de ongeoorloofde verstrekking van of toegang tot persoonsgegevens." },
          ],
        },
        {
          h: "2. Onderwerp, aard, doel en duur",
          body: "Verwerker verwerkt persoonsgegevens uitsluitend om de Diensten te leveren, te beveiligen en te ondersteunen, en om gedocumenteerde instructies van Verantwoordelijke uit te voeren. De DPA geldt zolang Verwerker persoonsgegevens namens Verantwoordelijke verwerkt en eindigt automatisch wanneer de hoofdovereenkomst eindigt en alle persoonsgegevens conform deze DPA zijn verwijderd of geretourneerd.",
        },
        {
          h: "3. Categorieën betrokkenen",
          tables: [
            {
              headers: ["Categorie", "Toelichting"],
              rows: [
                ["Medewerkers van Verantwoordelijke", "Gebruikers met een account in het platform"],
                ["Eindklanten van Verantwoordelijke", "Kopers en verkopers die de marketplace bezoeken of gebruiken"],
                ["Leveranciers", "Aanbieders van diensten of producten via de marketplace"],
                ["Contactpersonen van zakelijke partners", "Bedrijfscontacten verstrekt door Verantwoordelijke"],
              ],
            },
          ],
        },
        {
          h: "4. Categorieën persoonsgegevens",
          tables: [
            {
              headers: ["Categorie", "Voorbeelden"],
              rows: [
                ["Identificatie- en contactgegevens", "Naam, e-mail, telefoon, functie, bedrijfsnaam"],
                ["Account- en authenticatiegegevens", "Inlognaam, wachtwoord (gehasht), tweede factor"],
                ["Woninggerelateerde gegevens", "Adres, bouwjaar, energielabel, huispaspoort"],
                ["Communicatie", "Formulieren, support tickets, AI Wooncoach gesprekken"],
                ["Order- en transactiegegevens", "Bestellingen, prijzen, fees, betaalreferenties"],
                ["Technische gegevens", "IP-adres, browser, devicegegevens, logs"],
              ],
            },
          ],
        },
        {
          h: "5. Verplichtingen Verwerker",
          bullets: [
            "Verwerker verwerkt persoonsgegevens uitsluitend op basis van schriftelijke instructies van Verantwoordelijke, waaronder de hoofdovereenkomst en deze DPA.",
            "Verwerker zorgt dat personen die toegang hebben tot persoonsgegevens onder geheimhouding zijn gebonden.",
            "Verwerker informeert Verantwoordelijke onverwijld indien een instructie naar zijn mening in strijd is met de AVG of andere privacywetgeving.",
            "Verwerker stelt Verantwoordelijke desgevraagd alle informatie ter beschikking die nodig is om naleving van artikel 28 AVG aan te tonen.",
          ],
        },
        {
          h: "6. Beveiligingsmaatregelen",
          body: "Verwerker treft passende technische en organisatorische maatregelen, waaronder de maatregelen beschreven in Annex II.",
        },
        {
          h: "7. Subverwerkers",
          body: "Verantwoordelijke geeft Verwerker een algemene toestemming om subverwerkers in te schakelen voor onder meer hosting, e-mail, AI, betalingen en support. Een actuele lijst is opgenomen in Annex III. Bij voorgenomen wijzigingen in subverwerkers informeert Verwerker Verantwoordelijke ten minste 30 dagen vooraf, zodat Verantwoordelijke de gelegenheid heeft bezwaar te maken. Bij gegrond bezwaar zoeken partijen in goed overleg naar een passende oplossing; lukt dat niet, dan kan Verantwoordelijke de hoofdovereenkomst opzeggen voor het deel dat raakt aan de betreffende subverwerker.",
        },
        {
          h: "8. Internationale doorgifte",
          body: "Verwerker host persoonsgegevens primair binnen de EU. Indien doorgifte naar een land buiten de EER plaatsvindt, gebeurt dit op basis van de Standaard Contractuele Bepalingen van de Europese Commissie of een geldig adequaatheidsbesluit, aangevuld met aanvullende technische en organisatorische maatregelen waar nodig.",
        },
        {
          h: "9. Bijstand bij rechten van betrokkenen",
          body: "Verwerker biedt Verantwoordelijke redelijke ondersteuning, rekening houdend met de aard van de verwerking en de hem ter beschikking staande informatie, om te voldoen aan verzoeken van betrokkenen voor de uitoefening van hun rechten onder de AVG.",
        },
        {
          h: "10. Datalekken",
          body: "Verwerker informeert Verantwoordelijke zonder onnodige vertraging na ontdekking van een datalek dat persoonsgegevens van Verantwoordelijke betreft, en in elk geval tijdig genoeg om Verantwoordelijke in staat te stellen te voldoen aan haar 72-uurs meldplicht. De melding bevat in elk geval:",
          bullets: [
            "Een beschrijving van de aard van het datalek",
            "De (vermoedelijke) categorieën en aantallen betrokkenen en records",
            "De waarschijnlijke gevolgen",
            "De getroffen of voorgestelde maatregelen om de gevolgen te beperken",
            "Een contactpunt bij Verwerker voor meer informatie",
          ],
        },
        {
          h: "11. DPIA en voorafgaande raadpleging",
          body: "Verwerker biedt Verantwoordelijke redelijke ondersteuning bij gegevensbeschermingseffectbeoordelingen (DPIA's) en bij voorafgaande raadpleging van een toezichthoudende autoriteit, voor zover deze betrekking hebben op de verwerking onder deze DPA.",
        },
        {
          h: "12. Audit",
          body: "Tribeo is een klein bedrijf en faciliteert geen formele audits ter plaatse. Op redelijk verzoek verstrekken wij in plaats daarvan schriftelijk informatie waarmee Verantwoordelijke kan beoordelen of wij onze verplichtingen onder deze DPA nakomen, voor zover dat onze bedrijfsvoering niet onevenredig belast.",
        },
        {
          h: "13. Beëindiging",
          body: "Bij beëindiging van de Diensten verwijdert of retourneert Verwerker, naar keuze van Verantwoordelijke, alle persoonsgegevens, tenzij wetgeving een langere bewaarplicht oplegt. Backups worden binnen de standaard rotatietermijnen overschreven.",
        },
        {
          h: "14. Aansprakelijkheid",
          body: "De aansprakelijkheid van partijen onder deze DPA is onderworpen aan de aansprakelijkheidsbeperkingen in de hoofdovereenkomst, voor zover dat in overeenstemming is met de AVG.",
        },
        {
          h: "15. Wijzigingen en rangorde",
          body: "Bij strijdigheid tussen deze DPA en de hoofdovereenkomst prevaleert deze DPA voor zover het verwerking van persoonsgegevens betreft. Wijzigingen vereisen wederzijdse instemming, behoudens noodzakelijke aanpassingen die voortvloeien uit gewijzigde wetgeving.",
        },
        {
          h: "Annex I — Beschrijving van de verwerking",
          body: "Doel: levering van het Tribeo platform en marketplace. Aard: hosting, opslag, raadpleging, doorgifte, verwijdering. Duur: looptijd van de hoofdovereenkomst. Frequentie: continu. Categorieën zoals beschreven in artikelen 3 en 4.",
        },
        {
          h: "Annex II — Beveiligingsmaatregelen",
          bullets: [
            "Encryptie in transit (TLS) en at rest, geleverd door onze cloudleveranciers",
            "Toegang op need-to-know basis met multi-factor authenticatie voor productieomgevingen",
            "Wachtwoorden uitsluitend opgeslagen als gehashte waarden",
            "Logging van applicatie- en infrastructuuractiviteit",
            "Geautomatiseerde backups via onze cloudleveranciers",
            "Updates en patches op afhankelijkheden naar redelijk vermogen",
            "Verantwoorde-disclosure proces via security@tribeo.co",
            "Geen formele ISO/SOC certificeringen of pentest-programma op dit moment",
          ],
        },
        {
          h: "Annex III — Subverwerkers",
          tables: [
            {
              headers: ["Subverwerker", "Dienst", "Vestiging", "Doorgifte"],
              rows: [
                ["AWS", "Hosting, database, opslag", "EU (Frankfurt / Ierland)", "Binnen EER"],
                ["Google Cloud", "Aanvullende cloudinfrastructuur", "EU", "Binnen EER"],
                ["Resend", "Transactionele e-mail", "EU / VS", "SCC waar nodig"],
                ["Anthropic (Claude)", "AI / LLM voor Wooncoach", "VS", "SCC + minimale dataverstrekking"],
                ["Stripe", "Betalingen en uitbetalingen", "EU / VS", "SCC + PCI-DSS Level 1"],
              ],
            },
          ],
        },
      ],
      contact: [
        { label: "Verwerker", value: "TNA B.V., handelend als Tribeo" },
        { label: "Adres", value: "Santiagosingel 26, 2548HN 's-Gravenhage" },
        { label: "Privacy contact", value: "privacy@tribeo.co" },
        { label: "Security contact", value: "security@tribeo.co" },
      ],
      history: [
        { version: "v1.0", date: "5 mei 2026", note: "Eerste publicatie." },
      ],
    },
    security: {
      title: "Security",
      updated: "Laatst bijgewerkt",
      intro:
        "Tribeo is een klein team. Wij zijn eerlijk over wat wij wel en niet doen op het gebied van security. Wij leunen sterk op gevestigde leveranciers (AWS, Google, Stripe, Anthropic) en zorgen daarbovenop voor zorgvuldige toegangscontrole, encryptie en beheer van onze applicatie. Wij hebben (nog) geen ISO- of SOC-certificering en doen geen formele pentesten of audits.",
      sections: [
        {
          h: "1. Onze aanpak",
          body: "Wij doen ons best om Tribeo veilig op te zetten en draaiend te houden, zonder ons te willen vastpinnen op kaders die wij niet kunnen waarmaken. Waar mogelijk vertrouwen wij op de veiligheidsmaatregelen van onze leveranciers en houden wij onze eigen omgeving zo simpel en goed afgeschermd mogelijk.",
        },
        {
          h: "2. Hosting en infrastructuur",
          body: "Tribeo draait op infrastructuur van AWS en Google in de EU, via beheerde diensten. Daarmee profiteren wij van de fysieke beveiliging, redundantie en netwerkinrichting van deze leveranciers. Specifieke details over netwerksegmentatie, regio's en architectuur zijn afhankelijk van de keuzes en certificeringen van AWS en Google zelf.",
        },
        {
          h: "3. Encryptie",
          bullets: [
            "TLS voor al het verkeer richting onze diensten",
            "Encryptie at rest voor databases en opslag, geleverd door onze cloudleveranciers",
            "Wachtwoorden uitsluitend opgeslagen als gehashte waarden",
          ],
        },
        {
          h: "4. Toegang",
          bullets: [
            "Multi-factor authenticatie (MFA) voor accounts met toegang tot productiesystemen en gevoelige data",
            "Toegang op need-to-know basis: alleen mensen die het echt nodig hebben hebben toegang",
            "Periodieke check van wie toegang heeft tot wat",
          ],
        },
        {
          h: "5. Applicatie",
          body: "Wij ontwikkelen Tribeo met aandacht voor veiligheid en gebruiken moderne tools en frameworks. Wij doen geen formele penetratietests en hebben geen extern security-audit programma. Bekende kwetsbaarheden in afhankelijkheden proberen wij tijdig te updaten.",
        },
        {
          h: "6. Betalingen",
          body: "Betalingen worden afgehandeld door Stripe. Stripe is PCI DSS Level 1 gecertificeerd. Tribeo komt niet in aanraking met kaartgegevens.",
        },
        {
          h: "7. AI en LLM",
          body: "Wij gebruiken Claude (Anthropic) als LLM voor onder meer de AI Wooncoach. Anthropic verwerkt prompts onder hun eigen voorwaarden en privacyverklaring. Wij gebruiken AI niet om volledig automatische besluiten te nemen met juridische gevolgen voor gebruikers.",
        },
        {
          h: "8. Logging",
          body: "Wij houden technische logs bij van onze applicatie en infrastructuur om problemen te kunnen onderzoeken en de dienst te verbeteren. Logs worden niet langer bewaard dan nodig en zijn alleen toegankelijk voor het Tribeo team.",
        },
        {
          h: "9. Backups",
          body: "Wij maken gebruik van geautomatiseerde backups van onze databases via onze cloudleveranciers. Wij garanderen geen specifieke RPO of RTO, maar streven naar herstel binnen redelijke termijn bij incidenten.",
        },
        {
          h: "10. Subverwerkers",
          body: "Onze belangrijkste subverwerkers staan in de Verwerkersovereenkomst (DPA), Annex III. Voor hun beveiligingsmaatregelen verwijzen wij naar hun eigen documentatie en certificeringen.",
        },
        {
          h: "11. Incidenten en datalekken",
          body: "Bij een incident dat persoonsgegevens raakt volgen wij de procedure uit de AVG. Indien melding aan de Autoriteit Persoonsgegevens vereist is, doen wij dat binnen 72 uur. Klanten die het betreft informeren wij zo snel als redelijkerwijs mogelijk is.",
        },
        {
          h: "12. Verantwoorde disclosure",
          body: "Denk je dat je een kwetsbaarheid in Tribeo hebt gevonden? Mail dan vertrouwelijk naar security@tribeo.co. Wij waarderen meldingen en reageren zodra wij kunnen. Wij vragen onderzoekers om geen klantdata in te zien of te wijzigen, geen dienstverlening te verstoren en ons redelijke tijd te geven om de kwetsbaarheid te verhelpen.",
        },
        {
          h: "13. Wat wij (nog) niet hebben",
          body: "Eerlijk is eerlijk: Tribeo heeft op dit moment geen ISO 27001-, SOC 2- of vergelijkbare certificering, geen formeel BCM-programma, geen pentesten en geen externe security-audits. Wij doen ons best, maar geven daar geen garanties over. Wij geven dit eerlijk aan zodat klanten een goede inschatting kunnen maken.",
        },
        {
          h: "14. Privacy",
          body: "Voor de verwerking van persoonsgegevens zie de privacyverklaring en de Verwerkersovereenkomst (DPA).",
        },
      ],
      contact: [
        { label: "Security contact", value: "security@tribeo.co" },
        { label: "Privacy contact", value: "privacy@tribeo.co" },
        { label: "Verantwoordelijke", value: "TNA B.V., 's-Gravenhage" },
      ],
      history: [
        { version: "v1.0", date: "5 mei 2026", note: "Eerste publicatie." },
      ],
    },
  },
  en: {
    nav: {
      platform: "Platform",
      marketplaces: "Marketplaces",
      ai: "AI",
      pricing: "Pricing",
      contact: "Contact",
      about: "About",
      register: "Sign up free",
      login: "Log in",
    },
    hero: {
      eyebrow: "White-label platform for real estate agents",
      title: "After handover, your client walks out the",
      titleAccent: "door",
      subtitle:
        "One platform for everything around the home. Before, during and after the sale. Under your own brand, with vendors you select yourself. You earn a margin on every order. No subscription, no setup costs.",
      cta1: "Start for free",
      cta2: "Book a demo",
      badge: "No subscription. No setup costs.",
    },
    pillars: {
      title: "Four things Tribeo does for your office.",
      subtitle: "From marketplace to a personal property hub and tailored advice. One platform under your own brand, for the whole cycle around the home.",
      items: [
        { title: "Marketplace under your own brand", desc: "Your own storefront, your own domain and your own selection of vendors. You set the margin per product.", tone: "mint" },
        { title: "Property Hub for your client", desc: "Photos, floor plans, documents and a property timeline in one place. Transferable to the next owner when the home is sold.", tone: "coral" },
        { title: "AI Home Advisor", desc: "Personal advice on maintenance, renovation and sustainability. Linked straight to your vendors.", tone: "lavender" },
        { title: "One checkout, fully handled", desc: "Payments, VAT and payouts run automatically. No invoices to send, no chasing.", tone: "butter" },
      ],
    },
    audiences: {
      eyebrow: "Who it's for",
      title: "For the broker office and the supplier.",
      desc: "Tribeo has no subscription and no setup fees. Fully transparent: the supplier pays 6% on a successful sale. Nothing else.",
      tabs: [
        {
          tab: "Brokers",
          title: "Your own marketplace, on your own domain.",
          desc: "Build a lasting relationship with buyers and sellers via your own marketplace. Invite your own suppliers, or pick from suppliers already on Tribeo.",
          bullets: [
            "No subscription and no setup fees",
            "Your own domain, brand and product selection",
            "Unlimited marketplaces from one account",
            "Set margin per marketplace and per product",
          ],
        },
        {
          tab: "Suppliers",
          title: "One catalogue, multiple marketplaces.",
          desc: "Get a free webshop for your own customers. And become visible in the marketplaces of brokers that pass your products and services on to buyers and sellers.",
          bullets: [
            "Free webshop for your own customers",
            "Get selected by brokers in your area",
            "You set the price, terms and lead time",
            "You only pay on a successful sale (6%)",
          ],
        },
      ],
    },
    flow: {
      eyebrow: "How it works",
      title: "For buyers and sellers, it feels like one place.",
      desc: "Buyers and sellers order products and services from vendors the agent has selected.",
      steps: [
        { num: "01", title: "Open your agent's marketplace", desc: "Your brand, your domain. Familiar because it comes from the agent they already trust.", tone: "mint" },
        { num: "02", title: "Order or request a quote", desc: "Local vendors with clear pricing, terms and delivery times.", tone: "coral" },
        { num: "03", title: "Pay and done", desc: "Payment, invoice and payout are handled automatically.", tone: "lavender" },
      ],
    },
    broker: {
      eyebrow: "For agents",
      title: "You decide who is in your marketplace.",
      desc: "Set your own price and margin per product and per marketplace. Work with vendors you trust. Your brand, your clients, your rules.",
      bullets: ["Invite your own vendors or choose from the existing network", "Set your own margin per product or service", "Unlimited marketplaces from one account", "White label: your own logo, colours and domain"],
      invitedTitle: "Selected suppliers",
      invitedSuppliers: [
        { name: "Listing photography", tag: "Photo and video", status: "active" },
        { name: "Floorplanner", tag: "Floor plans and 3D", status: "active" },
        { name: "Stylist Studio", tag: "Home staging", status: "invited" },
        { name: "The Green Hand Gardens", tag: "Garden upkeep", status: "active" },
      ],
      marginLabel: "Your margin",
      storefronts: [
        { name: "Marketplace listing presentation", tag: "Photography, plans, staging", tone: "mint" },
        { name: "Marketplace sustainability", tag: "Insulation, heat pump, solar", tone: "lavender" },
        { name: "Marketplace new build Zuiderpark", tag: "Interior and garden", tone: "coral" },
      ],
      relationshipTitle: "You keep the client relationship. We handle the logistics.",
      relationshipDesc: "The buyer, seller or owner stays your client. Tribeo runs ordering, payments and payouts in the background. Communication carries your brand, not ours.",
      scaleTitle: "Scales with your office",
      scaleItems: [
        { t: "Solo agent", d: "One marketplace, your own domain, ready in ten minutes." },
        { t: "Office with a team", d: "Multiple colleagues, one shared dashboard, shared vendor selection." },
        { t: "Multiple branches", d: "A separate marketplace per branch, region or new-build project." },
      ],
    },
    supplier: {
      eyebrow: "For vendors",
      title: "Customers via warm referrals from agents.",
      desc: "On Tribeo you pay nothing per click and nothing per quote request. You appear in the marketplaces of real estate agents in your area, at the moment their clients need a garden, paintwork, photography or insulation. One catalogue, multiple marketplaces, plus a free webshop for your own clients. You pay 6 percent on a successful order. No sale, no costs.",
      bullets: [
        "Free webshop for your own clients",
        "Become visible to agents in your area",
        "You only pay when you sell (6%)",
        "Secure payout to your business account",
      ],
      stripeLabel: "Payments",
      stripeStatus: "Connected",
      productExample: { name: "Listing photography + floor plan", price: "€ 195", lead: "On site within 3 business days" },
      tile1Title: "Your own webshop", tile1Desc: "Free page for your own customers.",
      tile2Title: "Customers via brokers", tile2Desc: "Brokers send buyers and sellers to your offering.",
      tile3Title: "Secure payouts", tile3Desc: "Money in your business account after every order.",
      youDecide: "you decide",
      dashboardTitle: "One dashboard for every marketplace",
      dashboardDesc: "Manage your catalogue, prices and lead times in one place. Your products show up automatically with every agent who has selected you.",
      premiumTitle: "Optional: Premium visibility",
      premiumDesc: "Want extra prominence in the marketplaces of agents? You can opt into Premium later. For most vendors, joining for free is more than enough.",
      premiumBullets: [
        "Higher placement in marketplace search",
        "Recommended badge on relevant products",
        "Insight into which agents show your catalogue",
      ],
      premiumNote: "Premium is coming later. Start free, you can always opt in.",
    },
    sustainability: {
      eyebrow: "Sustainable living",
      title: "Sustainability, made easy.",
      desc: "Tribeo combines AI advice with suppliers selected by the broker. From insulation to heat pumps to solar, including subsidy checks.",
      items: [
        { title: "Insulation advice", desc: "Which package pays back for your home and area?" },
        { title: "Heat pump check", desc: "Does it fit your build year and energy use?" },
        { title: "Solar panels", desc: "Yield, payback and subsidy calculations." },
        { title: "Subsidy radar", desc: "Always up to date subsidies for your situation." },
      ],
    },
    how: {
      title: "Ten minutes from signup to live marketplace.",
      subtitle: "No developers, no integrations. Configure, invite vendors, launch.",
      steps: [
        { t: "Set your brand", d: "Logo, colours and domain. Your marketplace looks like you." },
        { t: "Select your vendors", d: "Invite your own partners or choose from the existing network." },
        { t: "Publish your marketplace", d: "Categories, products and margins in minutes." },
        { t: "Earn on every order", d: "Your margin is automatically settled with every transaction." },
      ],
    },
    payments: {
      eyebrow: "Payments",
      title: "Payments, splits and payouts. Handled.",
      desc: "One payment from the client. Two payouts, automatic. Your marketplace and the vendor each receive their share, with no invoice back-and-forth.",
      bullets: ["Split payments between marketplace and vendor", "Simple onboarding for vendors", "Automatic invoicing and VAT exports", "Real-time financial dashboards"],
      mock: {
        title: "Payouts this week",
        live: "Live",
        delta: "+12.4% vs last week",
        rows: [
          { who: "Heating co.", what: "Boiler check", amt: "€ 85.00" },
          { who: "Glazier", what: "HR++ glazing", amt: "€ 2,400.00" },
          { who: "Gardener", what: "Garden upkeep", amt: "€ 450.00" },
        ],
      },
    },
    ai: {
      eyebrow: "AI Home Advisor",
      title: "ChatGPT doesn't know your home. Tribeo does.",
      desc: "Year of construction, energy label, floor area, condition of the heating system. All available because the agent connects the property data. When a buyer asks a question, the AI Home Advisor answers based on that specific home. And it refers to vendors the agent has already vetted.",
      bullets: ["Direct answers to questions about your specific home", "Smart maintenance plan based on age and condition", "Sustainability advice including subsidy finder", "Pre-selected vendors ready to book"],
      modules: [
        { title: "AI Home Advisor", desc: "Ask anything about your home, from year of construction to energy label.", tone: "mint" },
        { title: "AI Renovation", desc: "Ideas, cost estimates and vendors in one conversation.", tone: "coral" },
        { title: "Smart Maintenance Plan", desc: "Timeline of maintenance based on age and condition.", tone: "butter" },
        { title: "AI Sustainability", desc: "Roadmap for insulation, heat pump, solar panels and subsidies.", tone: "lavender" },
      ],
      planTitle: "Smart Maintenance Plan",
      planItems: [
        { when: "Q4 2026", what: "Exterior painting. Time for a check." },
        { when: "Q2 2027", what: "Replace boiler (15 years old)." },
        { when: "2028", what: "Roof insulation. Payback around six years." },
      ],
      futureNote: "Coming soon: house passport and Vision AI. Scan a room with your phone and instantly see furniture from your suppliers.",
      mockAlt: "AI House Coach in a phone mockup",
    },
    pricing: {
      eyebrow: "Pricing",
      title: "No subscription. No setup costs. No contract.",
      desc: "The platform is free. Vendors pay 6 percent on a successful order. No sale means no costs. Stop at any time, no notice period.",
      columns: [
        { who: "Agent", price: "€ 0", sub: "No subscription, no setup costs.", tone: "mint" },
        { who: "Buyer and seller", price: "€ 0", sub: "Free to order and pay.", tone: "lavender" },
        { who: "Vendor", price: "6%", sub: "Only on a successful sale.", tone: "coral" },
      ],
      feeBadge: "6% transaction fee",
      feeNote: "You only pay when your clients transact. Payment processing costs may apply separately.",
      includes: "Everything included:",
      bullets: [
        "Unlimited branded marketplaces",
        "Unlimited vendors and products",
        "Secure payments and payouts",
        "AI Home Advisor, Renovation, Maintenance Plan and Sustainability",
        "Dashboards and reporting",
        "Email and chat support",
      ],
      cta: "Start for free",
      secondaryCta: "Book a demo",
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        { q: "What does Tribeo cost for an agent?", a: "Nothing. No subscription, no setup costs, no hidden fees. You use the platform for free and set your own margin per product. That margin is automatically transferred to your account with every transaction. The 6 percent transaction fee is charged to the vendor, not to you." },
        { q: "How do vendors receive payments?", a: "When registering, the vendor connects their business account once via Stripe. After that, every order is processed automatically. The client pays, the money is split between marketplace and vendor, and everything lands in the right account. The vendor receives clear reports and VAT-ready exports directly from the dashboard. No manual invoicing, no reminders." },
        { q: "Who selects the vendors?", a: "The agent, entirely on their own terms. You invite your own partners or choose from the network of vendors already on Tribeo. Per marketplace, you decide who is visible and at what margin. Tribeo does not interfere with that choice, beyond a basic check on Chamber of Commerce registration and Stripe verification." },
        { q: "Which properties is Tribeo suitable for?", a: "Almost anything an agent is involved with. Existing homes, new builds, apartments, detached houses, residential or commercial. Whether you want one marketplace for your whole agency or a separate one per project or region, it works." },
        { q: "What does the AI Home Advisor do exactly?", a: "The AI Home Advisor answers questions from buyers and sellers about their home. Maintenance, renovations, sustainability, costs, timelines. The difference from a generic chatbot is that it knows the specific home. Year of construction, floor area, energy label, age of the heating system. That context comes directly from the agent. The Home Advisor gives advice, not decisions with legal consequences. Under the hood it runs on Claude from Anthropic." },
        { q: "Does the vendor get property context with a quote?", a: "Only what is relevant to the quote, and only with the consent of the end client. A landscaper gets the garden surface area, not the energy label. An insulation company gets the year of construction and the wall dimensions, not the purchase price. This prevents a quote from being a blind guess, without sharing more than necessary." },
        { q: "Is my data secure?", a: "We do our best and are transparent about what we do and do not do. Encryption in transit and at rest, multi-factor authentication on production systems, hosting within the EU via AWS and Google. Payments run via Stripe, PCI DSS Level 1 certified. What we do not yet have: ISO 27001, SOC 2 or a formal penetration test programme. That is a deliberate choice for a small team that does not want to claim what it cannot back up. The full description is on the Security page." },
      ],
    },
    cta: {
      title: "Your client from last year will be at the hardware store in six months.",
      desc: "Build the marketplace where they would rather order. Ten minutes of work, no credit card needed.",
      button: "Start for free",
      secondary: "Book a demo",
    },
    hero_mock: {
      url: "marketplace.yourbroker.com",
      shopName: "Your Marketplace",
      items: [
        { name: "Listing photography", price: "€195", tone: "mint" },
        { name: "Floor plan + 3D", price: "€95", tone: "butter" },
        { name: "Home staging", price: "€450", tone: "coral" },
        { name: "Move-in advice", price: "Free", tone: "lavender" },
      ],
      payoutLabel: "Today's payout",
      payoutAmount: "€ 4,280.00",
      aiLabel: "AI House Coach",
      aiSnippet: "Your boiler is almost 15 years old. Time for service?",
      pill: "No subscription. 6% per transaction.",
    },
    footer: sharedFooter(true),
    demo: {
      title: "Request your personal demo",
      desc: "Fill in your details. We'll get back to you within one business day.",
      name: "Name", namePh: "First and last name",
      company: "Company", companyPh: "Company name",
      email: "Email", emailPh: "you@company.com",
      phone: "Phone (optional)", phonePh: "+31 …",
      message: "Message", messagePh: "Tell us briefly about your situation",
      submit: "Request demo", sending: "Sending…",
      success: "Thanks! We'll get back to you within one business day. Check your inbox for a confirmation.",
      error: "Something went wrong. Please check your details and try again.",
    },
    contact: {
      title: "Get in touch",
      subtitle: "We respond within one business day. For demos, partnerships and general questions.",
      emailLabel: "Email",
      addressLabel: "Address",
      kvkLabel: "Chamber of Commerce",
      formTitle: "Send us a message",
      submit: "Send message",
      sending: "Sending…",
      success: "Thanks. We'll get back to you within one business day. Check your inbox for a confirmation.",
      error: "Something went wrong. Please check your details and try again.",
    },
    os: {
      eyebrow: "One platform",
      title: "For agents and vendors, together.",
      desc: "A marketplace in your brand, payments, AI Home Advisor and webshops for your vendors. Tribeo connects supply and demand under your brand as the agent.",
      items: [
        { title: "Marketplace", desc: "Your domain, your brand, your product selection." },
        { title: "Payments", desc: "Splits and payouts, fully automatic." },
        { title: "AI Home Advisor", desc: "Answers, maintenance plan and sustainability advice." },
        { title: "Webshop for vendors", desc: "Every vendor gets a free webshop." },
      ],
      note: "Tribeo brings agents, vendors, buyers and sellers together under the agency's brand.",
    },
    about: {
      title: "About Tribeo",
      lead: "A real estate agent sells homes. Once. Everything that happens to that home afterwards passes them by. That is not right. The buyer who just purchased a home will spend tens of thousands on it in the first ten years. Staging, photography, a new kitchen, a landscaper, insulation, solar panels. At the moment of handover, they have no list and no opinions, only trust in their agent. After that, they end up on comparison sites, or with whoever paid the most for an ad. Tribeo fixes that. A marketplace under the brand of the agent, with vendors the agent has selected themselves. For the client it is one place to sort everything their new home needs. For the agent it is an extension of their service. For the vendor it is clients with budget and intent.",
      sections: [
        { h: "What we do", body: "We build the platform on which agents run their own marketplace. Their brand, their domain, their vendors, their margins. Vendors get a free webshop and become visible in the marketplaces of agents in their area. The technology behind it (payments, splits, AI Home Advisor, dashboards) runs in the background. On screen you see the agency, not us." },
        { h: "What we believe", body: "No subscription and no setup costs, not as a marketing trick but because we think a platform should only earn money when it delivers value. Our revenue is on transactions, not on lock-in. No long contracts. Want to stop? Stop today. Your catalogue and your data come with you. No hidden margins. What the agent sets is what the agent receives. What the vendor agrees to is what the vendor gets, minus the 6 percent we charge. No middle layer, no extra fee in the small print. Open technology. Tribeo is API first, and everything we use internally is documented for your IT partner. We do not lock anyone in. Honest about what we do not have. We are a small team in The Hague. No ISO 27001, no SOC 2, no formal penetration test programme. We run on AWS and Google in the EU, payments via Stripe, AI via Claude from Anthropic. The Security page describes exactly what we do and do not do, without polishing it up." },
        { h: "Who we do it for", body: "For the real estate agency that wants to keep helping clients after the transaction, without it becoming a side project. For the vendor who wants new clients without paying a comparison site every month for leads that may not convert. And for the buyer who wants one place to sort everything their new home needs, with people they already trust." },
        { h: "Behind Tribeo", body: "Tribeo is a product of TNA B.V., a Dutch company based at Santiagosingel 26, 2548HN in The Hague, registered with the Dutch Chamber of Commerce under number 75729814. We are independent, not backed by a large player in the property market, and we host in the EU. Questions, a demo or a conversation? hello@tribeo.co. We respond within one business day." },
      ],
    },
    trust: {
      eyebrow: "Secure and transparent",
      title: "No fancy words, just the basics in order.",
      desc: "We're a small team. We don't claim what we can't back up. Here's what we do.",
      items: [
        { t: "EU hosting", d: "Your data sits on servers inside the European Union." },
        { t: "Encrypted in transit and at rest", d: "By default, no extra settings required." },
        { t: "GDPR compliant", d: "Data processing per EU privacy rules, with a DPA on request." },
        { t: "Secure payments", d: "Payments through a PCI DSS Level 1 certified payment provider." },
      ],
      availability: "Tribeo is currently live in the Netherlands. EU rollout is planned.",
    },
    solutionsNav: {
      label: "Solutions",
      broker: "For agents",
      supplier: "For vendors",
    },
    solutions: {
      broker: {
        hero: {
          eyebrow: "For agents",
          title: "Your deal is done. Your client isn't.",
          lead: "The buyer you helped last month will spend money on a kitchen, a painter, a landscaper, insulation and new flooring in the coming years. Tens of thousands of euros, and right now none of that comes through you. With your own Tribeo marketplace, you refer those clients to vendors you have selected yourself, under your own brand. You earn a margin on every order. No subscription, no setup costs.",
          cta1: "Start for free",
          cta2: "Book a demo",
          image: "broker",
        },
        intro: {
          what: "A marketplace under your brand, with the vendors you have selected. Your logo, your colours, your domain. To your clients, it feels like part of your agency.",
          why: "The years after completion are more expensive for most buyers than the purchase itself. Renovations, maintenance, sustainability upgrades, interior work. Your client will spend that money regardless. The only question is whether they do it through a list you put together, or through Google and whoever paid most for the ad.",
        },
        benefits: {
          title: "Why Tribeo for your agency",
          items: [
            { t: "One deal, years of revenue", d: "The average buyer spends tens of thousands on their home in the first ten years after moving. That money goes elsewhere right now. With your own marketplace, you get a share of it.", tone: "mint" },
            { t: "Earn without extra work", d: "Setting your margin takes one click per product. After that, every order generates automatic income. Tribeo handles invoicing, VAT and payouts.", tone: "coral" },
            { t: "Your shortlist, not ours", d: "No marketplace full of vendors you do not know. You select who is visible, you set the price, you decide who gets access.", tone: "lavender" },
            { t: "Fully in your brand", d: "Your logo, your colours, your domain. The marketplace carries your name. Tribeo runs in the background.", tone: "butter" },
          ],
        },
        deepDive: {
          eyebrow: "Product detail",
          title: "What you get from the moment you create an account.",
          lead: "Every part is designed to make your work lighter and serve your clients better.",
          sections: [
            {
              title: "Your brand, everywhere",
              body: "Your own domain, your own logo, your own tone of voice in every email and page. Clients see your agency. Per marketplace, you choose which vendors and products are visible.",
              bullets: ["Own subdomain or fully custom domain", "Customisable brand identity and typography", "Your tone of voice in texts and emails"],
            },
            {
              title: "Unlimited marketplaces from one account",
              body: "A separate marketplace per new development project. One for sustainability. One per region or branch. Each with its own vendor selection, its own margins and its own look. You manage everything from one dashboard.",
              bullets: ["Separate marketplace per project or region", "Own vendors and margins per marketplace", "One dashboard for everything"],
            },
            {
              title: "You set your margin",
              body: "You set your margin per product and per marketplace. On every transaction, it is automatically settled and paid to your account. Example: the vendor prices home staging at €450. You set your margin at 15 percent. Your client pays €517.50. Tribeo transfers €67.50 to your account, automatically. The vendor receives their share after the 6 percent transaction fee. No invoice to send, no reminder to chase.",
              bullets: ["Margin configurable per product", "Transparent payouts", "VAT-ready exports"],
            },
            {
              title: "Works with the software you already use",
              body: "Tribeo is built API first. We connect with the systems you already work in, and we do not lock you in. Stop whenever you want, your data comes with you. Send the link to your IT partner, they will know what they are looking at.",
              bullets: ["Open API for integrations", "No vendor lock-in", "Documentation for your IT partner"],
            },
          ],
        },
        steps: {
          title: "How it works",
          items: [
            { num: "01", t: "Create a free account", d: "Name, email, Chamber of Commerce number. Done in ten minutes. No credit card, no contract." },
            { num: "02", t: "Select your vendors", d: "Invite your own partners or choose from the vendors already on Tribeo. Per marketplace, you decide who is visible and at what margin." },
            { num: "03", t: "Refer buyers and sellers", d: "Add the link to your email signature, your newsletter and your closing confirmation email. From that point, your clients order through your marketplace without you needing to do anything." },
            { num: "04", t: "Earn on every order", d: "Every transaction settles your margin automatically to your account. Tribeo handles the rest." },
          ],
        },
        faq: {
          title: "Frequently asked questions",
          items: [
            { q: "What do I earn per transaction?", a: "Whatever you set. You configure your own margin per product and per marketplace. Example: the vendor prices home staging at €450 and you set your margin at 15 percent. Your client pays €517.50 and Tribeo transfers €67.50 to your account. On photography, a kitchen quote or an insulation package it works exactly the same way. The 6 percent transaction fee is charged to the vendor, so what you set is what you receive." },
            { q: "Who manages the vendors?", a: "You do. You invite your own partners or choose vendors who are already on Tribeo. Tribeo handles the technology and payments, you manage the network and set the margins." },
            { q: "What does it cost?", a: "For you as an agent: zero. No subscription, no setup costs, no contract. The 6 percent transaction fee is charged to the vendor, not to you." },
            { q: "Can I create multiple marketplaces?", a: "Yes, unlimited. One for your whole agency, or a separate marketplace per new development project, region or branch. Each marketplace has its own vendor selection, its own margins and its own look. You manage everything from one dashboard." },
            { q: "Does Tribeo connect with my existing systems?", a: "Yes. We are built API first and have a documented open API. Send the link to your IT partner or developer and they will know exactly what they are working with. We do not lock you in. Your data is yours and you take it with you when you leave, with export and all." },
          ],
        },
      },
      supplier: {
        hero: {
          eyebrow: "For vendors",
          title: "Customers via warm referrals from agents.",
          lead: "On Tribeo you pay nothing per click and nothing per quote request. You appear in the marketplaces of real estate agents in your area, at the moment their clients need a garden, paintwork, photography or insulation. One catalogue, multiple marketplaces, plus a free webshop for your own clients. You pay 6 percent on a successful order. No sale, no costs.",
          cta1: "Start for free",
          cta2: "Book a demo",
          image: "supplier",
        },
        intro: {
          what: "A marketplace platform for agents and vendors. You fill in your catalogue once and become visible in all the marketplaces of agents who have selected you. On top of that, you get a free webshop for clients who want to order directly from you.",
          why: "Clients through the agent already know what they want. They have just moved or are about to move, they have budget, and they have an agent recommending you instead of someone who still needs to be convinced. No cold calling, no price hunters, no ads that cost money before anyone books.",
        },
        benefits: {
          title: "What you get",
          items: [
            { t: "Free webshop", d: "Your own page with your brand, your products and your terms. Send clients directly to your page or use it alongside your existing website. No subscription.", tone: "mint" },
            { t: "Clients through agents", d: "Agents in your area send buyers and sellers to their marketplace, and there you are. Your offer appears on screen at the moment the client is ready to book.", tone: "coral" },
            { t: "Secure payout", d: "You connect your business account to Stripe once. Every order arrives automatically in your account. No invoice to send, no reminder to chase.", tone: "lavender" },
            { t: "You only pay when you sell", d: "6 percent on a successful order. No subscription, no setup costs, no contract. No sale means no costs.", tone: "butter" },
          ],
        },
        deepDive: {
          eyebrow: "Product detail",
          title: "Build once. Sell everywhere.",
          lead: "Your catalogue, your prices, your terms. Tribeo makes sure you show up where the client is looking.",
          sections: [
            {
              title: "One catalogue, visible everywhere",
              body: "Fill in your products and services once. They appear in your own webshop and in all the marketplaces of agents who have selected you. Change a price or a delivery time and it updates everywhere immediately.",
              bullets: ["One place to manage your catalogue", "Automatically visible in agent marketplaces", "Your own prices, terms and delivery times"],
            },
            {
              title: "Your own webshop, free",
              body: "A free webshop on your own page for your own clients. Send clients directly, use it alongside your existing website, or as your only sales channel if you do not have a site yet. No developer needed, no hosting bill.",
              bullets: ["Your own page with your brand", "No technical knowledge required", "Works on laptop, tablet and phone"],
            },
            {
              title: "Secure payments and payouts",
              body: "You connect your business account once, via Stripe. After that, payment and payout happen automatically through a PCI DSS Level 1 certified payment service. Clear reports and VAT-ready exports are available straight from your dashboard.",
              bullets: ["One-time account connection", "Automatic payout per order", "VAT-ready invoices and exports"],
            },
            {
              title: "Fair and transparent",
              body: "No subscription, no setup costs. You pay 6 percent on a successful order. No sale means no costs. Stop at any time through your dashboard, your catalogue is immediately removed from all marketplaces.",
              bullets: ["6 percent only on successful sales", "No contract, no notice period", "Fully transparent in your dashboard"],
            },
          ],
        },
        steps: {
          title: "How to get started",
          items: [
            { num: "01", t: "Create a free account", d: "Name, email address, Chamber of Commerce number. Done in two minutes, no credit card needed." },
            { num: "02", t: "Fill in your products", d: "Photo, price, delivery time. If you get stuck, we will help you via email or chat." },
            { num: "03", t: "Connect your business account", d: "One time, via Stripe. From that point, every order arrives in your account automatically." },
          ],
        },
        faq: {
          title: "Frequently asked questions",
          items: [
            { q: "What does it cost?", a: "No subscription, no setup costs. You pay 6 percent on a successful order. No sale means no costs. Stop at any time through the dashboard." },
            { q: "Do I need technical knowledge?", a: "No. You fill in your products (photo, price, delivery time), connect your business account to Stripe and you are live. Onboarding takes about two minutes. If you get stuck, we will help you via email or chat." },
            { q: "Will I get clients through the agent?", a: "Not automatically, but that is what the whole platform is built for. Agents who have selected you in their marketplace send buyers and sellers in your area to your offer. How much that generates depends on how many agents you are connected to, how competitive your pricing and delivery times are, and how many transactions those agents complete each year. Since you only pay on a sale, there is no cost to being visible in multiple marketplaces at the same time." },
            { q: "How am I paid?", a: "With every successful order, the amount is transferred to your connected business account, after deduction of the 6 percent Tribeo transaction fee and any Stripe costs. Payout happens automatically according to the Stripe schedule, in practice within a few business days. Invoices and VAT-ready exports are available directly in your dashboard." },
            { q: "What if I want to stop?", a: "You close your account through the dashboard. Your catalogue is immediately removed from all marketplaces, open orders are handled cleanly. No exit fee, no hidden costs when leaving." },
          ],
        },
      },
    },
    privacy: {
      title: "Privacy Policy",
      updated: "Last updated",
      intro:
        "Tribeo is a trade name of TNA B.V. This privacy policy describes which personal data we process when you use the Tribeo platform, our websites (tribeo.co, tribeo.lovable.app) and the marketplaces that brokers offer under their own brand via Tribeo. We process personal data in accordance with the EU General Data Protection Regulation (GDPR) and the Dutch GDPR Implementation Act.",
      sections: [
        {
          h: "1. Who we are",
          body: "The controller for the processing described in this policy is TNA B.V., trading as Tribeo, located at Santiagosingel 26, 2548HN The Hague, the Netherlands, registered with the Chamber of Commerce under number 75729814. For privacy questions, data subject requests and complaints, contact privacy@tribeo.co.",
        },
        {
          h: "2. Scope",
          body: "This policy applies to:",
          bullets: [
            "Visits to and use of our websites and marketing pages",
            "Creating and using a Tribeo account (broker, supplier, end customer)",
            "Use of marketplaces offered by brokers under their own brand via Tribeo, where Tribeo acts as controller",
            "Use of the AI House Coach",
            "Communication with us via forms, email or support",
          ],
        },
        {
          h: "3. Definitions",
          body: "For readability we use the following terms:",
          definitions: [
            { term: "Personal data", def: "Any information relating to an identified or identifiable natural person." },
            { term: "Processing", def: "Any operation performed on personal data, such as collection, storage, consultation, transfer or erasure." },
            { term: "Controller", def: "The party that determines the purposes and means of the processing. For the processing described here, that is TNA B.V." },
            { term: "Processor", def: "A party that processes personal data on behalf of and on instructions from the controller." },
            { term: "Data subject", def: "The natural person to whom the personal data relates." },
          ],
        },
        {
          h: "4. Categories of personal data",
          body: "Depending on how you use Tribeo, we process the following categories of personal data.",
          tables: [
            {
              caption: "Overview of categories",
              headers: ["Category", "Examples", "Source"],
              rows: [
                ["Account and contact data", "Name, email, phone, role, company name, CoC number", "Provided by you on registration"],
                ["Authentication data", "Password (hashed), second factor, session tokens", "Created by you"],
                ["Profile and company data", "Logo, description, address, business hours", "Provided by you"],
                ["Transaction and invoice data", "Orders, prices, fees, VAT, IBAN reference via payment service", "Marketplace use"],
                ["Property data", "Address, year built, surface, energy label, house passport", "Provided by end customer or broker"],
                ["AI House Coach conversations", "Questions, answers, anonymised context", "Entered by you"],
                ["Communication", "Emails, form messages, support tickets", "Provided by you"],
                ["Technical data", "IP address, browser, device, language, page visits", "Collected automatically"],
                ["Cookie and analytics data", "Anonymised events after consent", "Automatic, based on consent"],
              ],
            },
          ],
        },
        {
          h: "5. Purposes and legal bases",
          body: "We only process personal data for the following purposes, each based on a valid legal basis under article 6 GDPR.",
          tables: [
            {
              headers: ["Purpose", "Legal basis", "Retention"],
              rows: [
                ["Create and manage account", "Performance of contract (art. 6.1.b)", "Account term + 24 months"],
                ["Provide the platform and marketplace", "Performance of contract", "Account term + 24 months"],
                ["Handle transactions and invoicing", "Performance of contract and legal obligation", "7 years (tax law)"],
                ["Customer support and communication", "Performance of contract and legitimate interest", "3 years after last contact"],
                ["Security, fraud prevention and logging", "Legitimate interest (art. 6.1.f)", "Up to 12 months"],
                ["Product improvement and anonymised analyses", "Legitimate interest", "Until purpose is reached"],
                ["Analytics and site measurement", "Consent (art. 6.1.a)", "Until withdrawn, max 14 months"],
                ["Direct marketing to business customers", "Legitimate interest with opt-out", "Until opt-out"],
                ["Marketing to prospects", "Consent", "Until withdrawn"],
                ["Compliance with legal obligations", "Legal obligation (art. 6.1.c)", "As required by law"],
              ],
            },
          ],
        },
        {
          h: "6. Cookies and similar techniques",
          body: "We use functional cookies (always on, for language, session and security) and analytics cookies that are only placed after you give consent via our cookie banner. We currently do not use third-party marketing or tracking cookies. See the cookie policy for details.",
        },
        {
          h: "7. AI House Coach",
          body: "The AI House Coach helps end customers with questions about maintenance, renovation and sustainability. We use Anthropic's Claude for this. The following principles apply:",
          bullets: [
            "Anthropic processes prompts under their own terms and privacy policy and, per their policy, does not by default use API input to train their models.",
            "We do not share more information with the AI provider than is necessary to answer a question.",
            "Answers from the AI House Coach are advisory; we do not make fully automated decisions with legal effects.",
            "We also use AI in a focused way in our own internal processes, for example as a tool for drafting or reviewing legal documents where useful.",
            "You can request deletion of your conversation history via privacy@tribeo.co.",
          ],
        },
        {
          h: "8. Marketing and communication",
          body: "Business customers may receive service and product updates based on legitimate interest, with an unsubscribe link in every email. For commercial newsletters to prospects we ask for prior consent. You can adjust your preferences at any time via the unsubscribe link in our emails or by contacting privacy@tribeo.co.",
        },
        {
          h: "9. Recipients and subprocessors",
          body: "We share personal data with the following categories of recipients, each under a data processing agreement:",
          tables: [
            {
              caption: "Main subprocessors",
              headers: ["Subprocessor", "Service", "Location", "Safeguard"],
              rows: [
                ["AWS", "Hosting, database, storage", "EU (Frankfurt / Ireland)", "DPA + EU hosting"],
                ["Google Cloud", "Additional cloud infrastructure", "EU", "DPA + EU hosting"],
                ["Resend", "Transactional email", "EU / US", "DPA + SCC"],
                ["Anthropic (Claude)", "AI / LLM for House Coach", "US", "DPA + SCC"],
                ["Stripe", "Payments and payouts", "EU / US", "PCI-DSS Level 1 + DPA"],
              ],
            },
          ],
        },
        {
          h: "10. International transfers",
          body: "We host primarily within the EU. When a subprocessor processes data outside the European Economic Area (EEA), this is only done with appropriate safeguards such as the European Commission's Standard Contractual Clauses or a valid adequacy decision, supplemented by technical and organisational measures where needed.",
        },
        {
          h: "11. Retention",
          body: "We do not retain personal data longer than necessary for the purposes for which they were collected or as required by law. See the table under purposes for specific terms.",
        },
        {
          h: "12. Security",
          body: "We take appropriate technical and organisational measures to protect personal data, such as encryption in transit and at rest, MFA for production access and logging. Tribeo is a small team and does not (yet) have ISO or SOC certification. See the Security page for what we do and do not (yet) do.",
        },
        {
          h: "13. Your rights",
          body: "Under the GDPR you have the following rights regarding your personal data:",
          subsections: [
            { h: "Access", body: "You can request which personal data we process about you." },
            { h: "Rectification", body: "You can have inaccurate or incomplete data corrected." },
            { h: "Erasure", body: "You can request deletion of personal data when no longer needed or when you withdraw consent." },
            { h: "Restriction", body: "You can ask us to temporarily restrict processing, for example while a request is reviewed." },
            { h: "Objection", body: "You can object to processing based on legitimate interest or for direct marketing." },
            { h: "Portability", body: "You can receive your data in a structured, common format or have it transferred to another party." },
            { h: "Withdraw consent", body: "Where we process based on consent, you can withdraw it at any time without retroactive effect." },
            { h: "Lodge a complaint", body: "You can lodge a complaint with the Dutch Data Protection Authority." },
          ],
        },
        {
          h: "14. How to exercise your rights",
          body: "Send a request to privacy@tribeo.co. We will respond within one month in principle. If we need more time, we will inform you in good time. We may ask for additional information to verify your identity before granting a request.",
        },
        {
          h: "15. Automated decision-making",
          body: "We do not use solely automated decision-making with legal or similarly significant effects on data subjects. The AI House Coach provides advisory information; decisions remain with the user.",
        },
        {
          h: "16. Children",
          body: "The Tribeo platform is not aimed at persons under 16. We do not knowingly process data of minors without parental or guardian consent. If this happens unintentionally, please contact us so we can remove the data.",
        },
        {
          h: "17. Changes",
          body: "We may amend this privacy policy when our service or applicable law changes. For material changes we will inform you in advance, for example by email or a notice on the platform. The most recent version is always on this page.",
        },
        {
          h: "18. Complaints",
          body: "We would like to hear from you if you are not satisfied with how we handle your data: privacy@tribeo.co. You also always have the right to lodge a complaint with the Dutch Data Protection Authority (autoriteitpersoonsgegevens.nl) or with the supervisory authority in your EU member state.",
        },
      ],
      contact: [
        { label: "Controller", value: "TNA B.V., trading as Tribeo" },
        { label: "Address", value: "Santiagosingel 26, 2548HN The Hague" },
        { label: "CoC", value: "75729814" },
        { label: "Privacy contact", value: "privacy@tribeo.co" },
        { label: "Security contact", value: "security@tribeo.co" },
      ],
      history: [
        { version: "v1.0", date: "May 5, 2026", note: "Initial publication." },
      ],
    },
    terms: {
      title: "Terms of Service",
      updated: "Last updated",
      intro:
        "These terms apply to the use of the Tribeo platform and related services, provided by TNA B.V. (trading as Tribeo), located at Santiagosingel 26, 2548HN The Hague, CoC 75729814. By creating an account or using the platform you accept these terms.",
      sections: [
        {
          h: "1. Definitions",
          body: "The following definitions apply:",
          definitions: [
            { term: "Tribeo / we / us", def: "TNA B.V., trading as Tribeo." },
            { term: "Platform", def: "The Tribeo SaaS platform including marketplaces, dashboards, AI House Coach, payment integration and APIs." },
            { term: "Customer", def: "The business party that enters into an agreement with Tribeo (broker or supplier) and has an account." },
            { term: "Broker", def: "Customer using the platform to offer its own marketplace to end customers." },
            { term: "Supplier", def: "Customer offering services or products via one or more marketplaces." },
            { term: "End customer", def: "The natural person (home buyer or seller) who orders or requests a quote via a marketplace." },
            { term: "Agreement", def: "The contract between Tribeo and the Customer, consisting of these terms, any order confirmation and the applicable DPA." },
            { term: "Transaction fee", def: "The percentage Tribeo charges on each successful transaction between End customer and Supplier." },
          ],
        },
        {
          h: "2. Applicability and order of precedence",
          body: "These terms apply to every agreement and use of the platform. Customer's general terms and conditions are expressly rejected. In case of conflict the following order applies: (1) a written specific agreement between the parties, (2) the Data Processing Agreement (DPA), (3) these general terms, (4) the product documentation.",
        },
        {
          h: "3. Account and registration",
          body: "To use the platform, Customer creates an account independently. Customer warrants that all data provided on registration is accurate and complete and keeps it up to date. The platform is intended for business use only by legal entities or self-employed entrepreneurs aged 18 or over. Customer is responsible for keeping login credentials secret and for all actions under the account.",
        },
        {
          h: "4. Availability",
          body: "Tribeo provides the platform with reasonable effort and aims for high availability. We cannot guarantee uninterrupted access.",
          subsections: [
            { h: "Maintenance", body: "Planned maintenance is announced in advance and scheduled outside business hours where possible." },
            { h: "Outages", body: "In case of unplanned outages we will use reasonable efforts to resolve them as soon as possible. We are not liable for the consequences of temporary unavailability, except in case of intent or gross negligence." },
            { h: "Functionality changes", body: "We may add, change or end functionality. Material limitations are announced at least 30 days in advance." },
          ],
        },
        {
          h: "5. Role of Tribeo",
          body: "Tribeo solely provides the technical infrastructure with which Brokers can offer marketplaces and Suppliers can sell their offering. Tribeo's role is facilitating only and Tribeo is expressly not a party to the agreements concluded via the platform between Suppliers and End customers (predominantly between consumers and businesses). Tribeo is not responsible or liable for services or products supplied or not supplied, nor for their performance, quality, safety, accuracy or warranties. Tribeo gives no warranties whatsoever regarding the offering on the platform. Tribeo does its best to monitor quality on the platform and is available on a best-effort basis to help with issues, without thereby assuming any obligation to deliver a specific result.",
        },
        {
          h: "6. Customer obligations",
          body: "Customer uses the platform only for the agreed business purposes and complies with applicable laws and regulations, including consumer law, tax rules and privacy law. Customer is responsible for the content placed via the account.",
        },
        {
          h: "7. Supplier obligations",
          body: "The Supplier is fully and solely responsible for:",
          bullets: [
            "Accuracy of prices, descriptions, lead times and stock information",
            "Delivery of ordered services or products as agreed",
            "Any warranties, right of withdrawal and consumer rights",
            "Customer service, complaints handling and aftersales",
            "Compliance with tax and industry-specific obligations",
            "Liability towards the End customer for own acts or omissions",
          ],
        },
        {
          h: "8. End customers and consumer law",
          body: "When an End customer purchases a service or product as a consumer via a marketplace, statutory consumer rights apply against the Supplier. The Supplier is responsible for complying with the right of withdrawal, statutory conformity and any warranty. Tribeo only facilitates the transaction via Stripe and may, on a best-effort basis, support escalations, but is not a party to the purchase agreement and bears no responsibility for the outcome.",
        },
        {
          h: "9. Pricing, fees and payment",
          body: "The platform has no subscription and no setup fees for Brokers, Suppliers and End customers. Payments are processed via Stripe. In addition to these terms, Stripe's terms apply to payments via Stripe.",
          subsections: [
            { h: "Transaction fee", body: "Tribeo charges a 6% transaction fee on each successful Supplier transaction via a marketplace, excluding VAT." },
            { h: "Stripe costs", body: "Stripe costs may apply separately and are shown transparently. Stripe's terms and privacy policy apply to payment processing." },
            { h: "Payout", body: "Payout to the Supplier takes place via Stripe, after deduction of fees, to the business account provided by the Supplier in Stripe, in line with Stripe's payout schedules." },
            { h: "VAT and invoicing", body: "Invoices are made available electronically. Customer is responsible for accurate VAT data." },
            { h: "Refunds and chargebacks", body: "On refunds to End customers, related fees are settled proportionally. Chargebacks are handled by Stripe and may be passed on to the responsible Supplier." },
          ],
        },
        {
          h: "10. Acceptable use",
          body: "It is prohibited to use the platform for, among other things:",
          bullets: [
            "Unlawful, misleading or infringing content",
            "Bypassing Tribeo's transaction handling",
            "Automated extraction or scraping without written consent",
            "Reverse engineering, decompiling or attempts to obtain source code",
            "Sending spam or unsolicited messages via the platform",
            "Activities that compromise the security or integrity of the platform",
          ],
        },
        {
          h: "11. Intellectual property",
          body: "All intellectual property rights in the platform, the software, documentation and the Tribeo trademarks belong to TNA B.V. Customer receives a non-exclusive, non-transferable right to use the platform for the duration of the agreement. Customer and Supplier retain rights to content they upload and grant Tribeo a worldwide, royalty-free licence to use that content as necessary to provide the service.",
        },
        {
          h: "12. Confidentiality",
          body: "Parties treat confidential information of the other party confidentially and use it only for performing the agreement. This obligation survives termination of the agreement.",
        },
        {
          h: "13. Privacy and data processing",
          body: "For the processing of personal data we refer to the privacy policy. Where Tribeo processes personal data on behalf of the Customer, the parties enter into the Data Processing Agreement (DPA), which is an integral part of the agreement.",
        },
        {
          h: "14. Security",
          body: "Tribeo takes appropriate technical and organisational measures to secure the platform and the data processed on it. See the Security page for details.",
        },
        {
          h: "15. Warranties and disclaimer",
          body: "Tribeo provides the platform 'as is' and 'as available'. We do not warrant that the platform will be error-free or uninterrupted or fit for a specific purpose beyond the documented functionality. To the extent permitted by law, all implied warranties are excluded.",
        },
        {
          h: "16. Liability",
          body: "Tribeo's liability is limited as follows:",
          bullets: [
            "Liability per event or series of related events is limited to the transaction fees Tribeo received from the Customer in the three months preceding the event, with a maximum of € 10,000.",
            "Liability for indirect damages, consequential losses, loss of profits, loss of data or reputational damage is excluded.",
            "Limitations do not apply in case of intent or wilful recklessness of Tribeo or its management, or for statutory liability that cannot be excluded.",
          ],
        },
        {
          h: "17. Indemnity",
          body: "Customer and Supplier indemnify Tribeo against third-party claims, including those of End customers, arising from their use of the platform or services or products they offer.",
        },
        {
          h: "18. Force majeure",
          body: "Neither party is liable for failures resulting from force majeure, including outages at subprocessors, internet outages, cyberattacks, government measures or natural disasters. In case of prolonged force majeure (more than 30 days) parties may terminate the agreement without compensation.",
        },
        {
          h: "19. Term and termination",
          body: "The agreement is for an indefinite period and may be terminated by either party at any time via the dashboard, without notice period. Open orders will be processed properly; a supplier's catalogue is removed from all marketplaces immediately. Tribeo may suspend or terminate the account with immediate effect in case of serious breach of these terms, repeated payment default, fraud or abuse.",
        },
        {
          h: "20. Changes to the terms",
          body: "Tribeo may amend these terms. Material changes are announced at least 30 days in advance via email or via the platform. If the Customer does not accept the change, it can terminate the agreement free of charge before the effective date.",
        },
        {
          h: "21. Assignment",
          body: "Customer may not assign rights and obligations under the agreement to a third party without Tribeo's prior written consent. Tribeo may assign the agreement in the context of a merger, acquisition or reorganisation.",
        },
        {
          h: "22. Miscellaneous",
          body: "If any provision of these terms is null or voidable, the remaining provisions remain in full force. Failure to exercise a right by Tribeo does not constitute a waiver of that right. These terms, together with the DPA and any order confirmation, constitute the entire agreement between the parties.",
        },
        {
          h: "23. Governing law and disputes",
          body: "These terms and all agreements with Tribeo are governed by Dutch law. Disputes are exclusively submitted to the competent court in The Hague, unless mandatory law provides otherwise.",
        },
      ],
      contact: [
        { label: "Provider", value: "TNA B.V., trading as Tribeo" },
        { label: "Address", value: "Santiagosingel 26, 2548HN The Hague" },
        { label: "CoC", value: "75729814" },
        { label: "Contact", value: "hello@tribeo.co" },
      ],
      history: [
        { version: "v1.0", date: "May 5, 2026", note: "Initial publication." },
      ],
    },
    cookies: {
      title: "Cookie Policy",
      updated: "Last updated",
      intro: "Tribeo uses cookies and similar techniques to make the website work and, only with your consent, analyse usage. We currently do not use marketing cookies.",
      sections: [
        { h: "1. What cookies are", body: "Small text files placed on your device when you visit a website." },
        { h: "2. Categories", body: "We use:", bullets: ["Functional (always on): language, session. Up to 12 months.", "Analytics (consent based): anonymised click tracking.", "Marketing: not currently in use."] },
        { h: "3. Third parties", body: "Stripe places necessary cookies for checkout and fraud prevention." },
        { h: "4. Withdraw consent", body: "Via your browser or the cookie settings." },
        { h: "5. More info", body: "See our Privacy Policy. Questions? hello@tribeo.co." },
      ],
    },
    dpa: {
      title: "Data Processing Agreement (DPA)",
      updated: "Last updated",
      intro:
        "This Data Processing Agreement (DPA) is an integral part of the agreement between TNA B.V. (trading as Tribeo, the Processor) and the business customer (the Controller). It describes how Processor processes personal data on behalf of Controller in accordance with article 28 GDPR. In case of conflict this DPA prevails over the general terms in respect of the processing of personal data.",
      sections: [
        {
          h: "1. Definitions",
          body: "Terms from the GDPR have the same meaning in this DPA. In addition:",
          definitions: [
            { term: "GDPR", def: "Regulation (EU) 2016/679 (General Data Protection Regulation)." },
            { term: "Services", def: "The Tribeo platform and related supporting services." },
            { term: "Subprocessor", def: "A third party engaged by Processor that processes personal data on behalf of Controller." },
            { term: "Personal data breach", def: "A breach of security leading to the accidental or unlawful destruction, loss, alteration or unauthorised disclosure of, or access to, personal data." },
          ],
        },
        {
          h: "2. Subject matter, nature, purpose and duration",
          body: "Processor processes personal data only to provide, secure and support the Services and to carry out documented instructions from Controller. The DPA applies for as long as Processor processes personal data on behalf of Controller and ends automatically when the main agreement ends and all personal data has been deleted or returned in accordance with this DPA.",
        },
        {
          h: "3. Categories of data subjects",
          tables: [
            {
              headers: ["Category", "Description"],
              rows: [
                ["Employees of Controller", "Users with an account in the platform"],
                ["End customers of Controller", "Buyers and sellers visiting or using the marketplace"],
                ["Suppliers", "Providers of services or products via the marketplace"],
                ["Business contacts", "Contacts provided by Controller"],
              ],
            },
          ],
        },
        {
          h: "4. Categories of personal data",
          tables: [
            {
              headers: ["Category", "Examples"],
              rows: [
                ["Identification and contact data", "Name, email, phone, role, company name"],
                ["Account and authentication data", "Username, password (hashed), second factor"],
                ["Property-related data", "Address, year built, energy label, house passport"],
                ["Communication", "Forms, support tickets, AI House Coach conversations"],
                ["Order and transaction data", "Orders, prices, fees, payment references"],
                ["Technical data", "IP address, browser, device data, logs"],
              ],
            },
          ],
        },
        {
          h: "5. Processor obligations",
          bullets: [
            "Processor processes personal data only on the basis of written instructions from Controller, including the main agreement and this DPA.",
            "Processor ensures that persons with access to personal data are bound by confidentiality.",
            "Processor informs Controller without delay if, in its opinion, an instruction infringes the GDPR or other privacy law.",
            "Processor makes available to Controller, on request, all information necessary to demonstrate compliance with article 28 GDPR.",
          ],
        },
        {
          h: "6. Security measures",
          body: "Processor implements appropriate technical and organisational measures, including the measures described in Annex II.",
        },
        {
          h: "7. Subprocessors",
          body: "Controller gives Processor a general authorisation to engage subprocessors for, among other things, hosting, email, AI, payments and support. A current list is included in Annex III. For intended changes in subprocessors, Processor will notify Controller at least 30 days in advance, allowing Controller to object. In case of justified objection, parties will seek a suitable solution in good consultation; if not possible, Controller may terminate the main agreement for the part affected by the relevant subprocessor.",
        },
        {
          h: "8. International transfers",
          body: "Processor primarily hosts personal data within the EU. If transfer to a country outside the EEA takes place, this is done on the basis of the European Commission's Standard Contractual Clauses or a valid adequacy decision, supplemented by additional technical and organisational measures where necessary.",
        },
        {
          h: "9. Assistance with data subject rights",
          body: "Processor provides reasonable assistance to Controller, taking into account the nature of the processing and the information available to it, to comply with requests from data subjects exercising their rights under the GDPR.",
        },
        {
          h: "10. Personal data breaches",
          body: "Processor informs Controller without undue delay after becoming aware of a personal data breach involving Controller's personal data, and in any case in time to enable Controller to comply with its 72-hour notification obligation. The notification at least contains:",
          bullets: [
            "A description of the nature of the breach",
            "The (likely) categories and approximate numbers of data subjects and records",
            "The likely consequences",
            "The measures taken or proposed to mitigate the consequences",
            "A contact point at Processor for more information",
          ],
        },
        {
          h: "11. DPIA and prior consultation",
          body: "Processor provides reasonable assistance to Controller with data protection impact assessments (DPIAs) and with prior consultation of a supervisory authority, where these relate to the processing under this DPA.",
        },
        {
          h: "12. Audit",
          body: "Tribeo is a small company and does not facilitate formal on-site audits. On reasonable request, we will instead provide written information that allows Controller to assess whether we comply with our obligations under this DPA, to the extent this does not unduly burden our operations.",
        },
        {
          h: "13. Termination",
          body: "On termination of the Services, Processor will, at Controller's choice, delete or return all personal data, unless law imposes a longer retention obligation. Backups are overwritten within standard rotation periods.",
        },
        {
          h: "14. Liability",
          body: "Liability of the parties under this DPA is subject to the liability limitations in the main agreement, to the extent consistent with the GDPR.",
        },
        {
          h: "15. Changes and order of precedence",
          body: "In case of conflict between this DPA and the main agreement, this DPA prevails as far as the processing of personal data is concerned. Changes require mutual consent, except for necessary adjustments resulting from changed legislation.",
        },
        {
          h: "Annex I — Description of the processing",
          body: "Purpose: provision of the Tribeo platform and marketplace. Nature: hosting, storage, consultation, transfer, deletion. Duration: term of the main agreement. Frequency: continuous. Categories as described in articles 3 and 4.",
        },
        {
          h: "Annex II — Security measures",
          bullets: [
            "Encryption in transit (TLS) and at rest, provided by our cloud vendors",
            "Need-to-know access with multi-factor authentication for production environments",
            "Passwords stored only as hashed values",
            "Logging of application and infrastructure activity",
            "Automated backups via our cloud vendors",
            "Updates and patches on dependencies to the best of our ability",
            "Responsible disclosure process via security@tribeo.co",
            "No formal ISO/SOC certifications or pentest programme at this time",
          ],
        },
        {
          h: "Annex III — Subprocessors",
          tables: [
            {
              headers: ["Subprocessor", "Service", "Location", "Transfer"],
              rows: [
                ["AWS", "Hosting, database, storage", "EU (Frankfurt / Ireland)", "Within EEA"],
                ["Google Cloud", "Additional cloud infrastructure", "EU", "Within EEA"],
                ["Resend", "Transactional email", "EU / US", "SCC where applicable"],
                ["Anthropic (Claude)", "AI / LLM for House Coach", "US", "SCC + minimal data sharing"],
                ["Stripe", "Payments and payouts", "EU / US", "SCC + PCI-DSS Level 1"],
              ],
            },
          ],
        },
      ],
      contact: [
        { label: "Processor", value: "TNA B.V., trading as Tribeo" },
        { label: "Address", value: "Santiagosingel 26, 2548HN The Hague" },
        { label: "Privacy contact", value: "privacy@tribeo.co" },
        { label: "Security contact", value: "security@tribeo.co" },
      ],
      history: [
        { version: "v1.0", date: "May 5, 2026", note: "Initial publication." },
      ],
    },
    security: {
      title: "Security",
      updated: "Last updated",
      intro:
        "Tribeo is a small team. We are honest about what we do and do not do regarding security. We rely heavily on established providers (AWS, Google, Stripe, Anthropic) and on top of that we apply careful access control, encryption and management of our application. We do not (yet) have ISO or SOC certification and we do not run formal pentests or external audits.",
      sections: [
        {
          h: "1. Our approach",
          body: "We do our best to set up and run Tribeo securely, without committing to frameworks we cannot live up to. Where possible, we rely on the security measures of our providers and keep our own environment as simple and well-isolated as possible.",
        },
        {
          h: "2. Hosting and infrastructure",
          body: "Tribeo runs on AWS and Google infrastructure in the EU, via managed services. As a result, we benefit from these providers' physical security, redundancy and network setup. Specific details about network segmentation, regions and architecture depend on the choices and certifications of AWS and Google themselves.",
        },
        {
          h: "3. Encryption",
          bullets: [
            "TLS for all traffic to our services",
            "Encryption at rest for databases and storage, provided by our cloud vendors",
            "Passwords stored only as hashed values",
          ],
        },
        {
          h: "4. Access",
          bullets: [
            "Multi-factor authentication (MFA) for accounts with access to production systems and sensitive data",
            "Need-to-know access: only people who really need access have access",
            "Periodic check of who has access to what",
          ],
        },
        {
          h: "5. Application",
          body: "We develop Tribeo with attention to security, using modern tools and frameworks. We do not run formal penetration tests and we do not have an external security audit programme. We try to update known vulnerabilities in dependencies in a timely manner.",
        },
        {
          h: "6. Payments",
          body: "Payments are processed by Stripe. Stripe is PCI DSS Level 1 certified. Tribeo does not handle card data.",
        },
        {
          h: "7. AI and LLM",
          body: "We use Claude (Anthropic) as the LLM for, among other things, the AI House Coach. Anthropic processes prompts under their own terms and privacy policy. We do not use AI to make fully automated decisions with legal effects on users.",
        },
        {
          h: "8. Logging",
          body: "We keep technical logs of our application and infrastructure to investigate issues and improve the service. Logs are not retained longer than necessary and are only accessible to the Tribeo team.",
        },
        {
          h: "9. Backups",
          body: "We use automated backups of our databases via our cloud providers. We do not guarantee a specific RPO or RTO, but we aim for recovery within a reasonable time in case of incidents.",
        },
        {
          h: "10. Subprocessors",
          body: "Our main subprocessors are listed in the Data Processing Agreement (DPA), Annex III. For their security measures we refer to their own documentation and certifications.",
        },
        {
          h: "11. Incidents and personal data breaches",
          body: "In the event of an incident affecting personal data, we follow the procedure under the GDPR. Where notification to the Dutch Data Protection Authority is required, we do so within 72 hours. We inform affected customers as soon as reasonably possible.",
        },
        {
          h: "12. Responsible disclosure",
          body: "Think you have found a vulnerability in Tribeo? Email us confidentially at security@tribeo.co. We appreciate reports and respond as soon as we can. We ask researchers not to access or modify customer data, not to disrupt service and to give us reasonable time to remediate the vulnerability.",
        },
        {
          h: "13. What we do not (yet) have",
          body: "Honestly: Tribeo currently has no ISO 27001, SOC 2 or comparable certification, no formal BCM programme, no pentests and no external security audits. We do our best, but make no guarantees about that. We state this openly so customers can make a fair assessment.",
        },
        {
          h: "14. Privacy",
          body: "For the processing of personal data, see the privacy policy and the Data Processing Agreement (DPA).",
        },
      ],
      contact: [
        { label: "Security contact", value: "security@tribeo.co" },
        { label: "Privacy contact", value: "privacy@tribeo.co" },
        { label: "Controller", value: "TNA B.V., The Hague" },
      ],
      history: [
        { version: "v1.0", date: "May 5, 2026", note: "Initial publication." },
      ],
    },
  },
};

export type Translation = Dict;
